// <vf-apy-chart>: realized net delegator APY vs cluster, per epoch.
//
// Attributes: api-base (required), from-epoch, to-epoch, theme (light|dark).
// Renders inside shadow DOM. Fetches /apy, /cluster, /income; any failure
// shows an inline error state, never a silent empty chart.
//
// One rendering: the full instrument — header with controls, answer band,
// chart, pin lane, footer. (The former compact
// variant="card" tile and its vf-expand contract are removed; a stray
// `variant` attribute on an old embed is ignored.) The header's
// "explorer" control (base mode only) swaps every chart surface for an
// embedded <vf-income-explorer>, created lazily on first toggle; toggling
// back restores the same view and zoom window.

import { closeIcon } from "./icons";
import * as echarts from "echarts/core";
import { BarChart, CustomChart, LineChart, ScatterChart } from "echarts/charts";
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import type { EChartsType } from "echarts/core";

import {
  ApiError,
  createClient,
  type EpochSummary,
  type ScheduleSlot,
  type SkipsRow,
} from "./api";
import { BlocksStreamPanel, type StreamSnapshot } from "./blocks-stream";
import { buildTrackTicks, renderLiveTrack } from "./live-track";
import {
  buildOption,
  SERIES_OUR,
  SERIES_OUR_BASE,
  SERIES_OUR_NET_DASHED,
  type ChartLayout,
  type ChartLayoutTier,
  type ZoomWindow,
  defaultZoomWindow,
} from "./chart-options";
import {
  ordinal,
  percentileFloor,
  percentileTooltipLines,
  trailingMedianByDays,
  trailingRealizedApy,
} from "./consistency";
import { DrilldownPanel } from "./drilldown";
import {
  formatAboveBestBps,
  formatBps,
  formatCount,
  formatDeltaWords,
  formatEconomicDeltaSigned,
  formatEconomicGapApprox,
  formatPct,
  formatSignedPp,
  formatUtcDateTime,
  formatUtcMonthDay,
  formatWithinBps,
} from "./format";
import { createFetchJson, EXPLORER_CLOSE_EVENT, fetchEpochBlocks } from "./income-explorer";
// Type-only: the runtime element is still created lazily by tag name.
import type { VfIncomeExplorer } from "./vf-income-explorer";
import {
  buildEtaSegments,
  buildLiveSegments,
  LIVE_TICK_MS,
  mergeLiveFacts,
  renderLiveStrip,
  renderLiveUnavailable,
  shouldBlinkCursor,
  type LiveFacts,
} from "./live-strip";
import {
  epochChannelPoint,
  epochTooltipTitle,
  epochTooltipView,
} from "./income-explorer-chart";
import {
  buildIncomeOption,
  INCOME_CHANNEL_ORDER,
  INCOME_TIPS_FOOTNOTE,
  INCOME_USD_FOOTNOTE,
  incomeUsdTooltipTitle,
  incomeUsdTooltipView,
  usdFieldsPresent,
  type IncomeChannel,
  type IncomeUnit,
} from "./income-view";
import { PinLayer, type PinData } from "./pins";
import {
  assemble,
  distributionCoverage,
  distributionCoverageInfl,
  hasCoveredEpoch,
  hasCoveredEpochBase,
  selectHeadlineEpoch,
  selectHeadlineEpochBase,
  type Assembled,
  type HeadlineEpoch,
} from "./series";
import { mevComponentPp, type StatsBasis } from "./stats";
import { STYLES } from "./styles";
import { themeTokens, type ThemeTokens } from "./theme";
import {
  buildMevOption,
  buildPositionOption,
  MEV_VIEW_FOOTNOTE,
  mevTooltipLines,
  positionViewFootnote,
  type ChartView,
} from "./view-options";
import {
  buildEpochCopyPayload,
  buildEpochView,
  coverageGateReason,
  epochHeader,
  epochHeaderShort,
  viewToHtml,
} from "./tooltip";

echarts.use([
  LineChart,
  CustomChart,
  ScatterChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  DataZoomComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  SVGRenderer,
]);

// Raised from 700 (itself raised from 640 with the C51 label rename):
// measured at 700 the full row — title + meta + view switch + [validator
// income] + [explorer] + theme toggle — still needs ~810px, so the meta
// survived its own collapse threshold and the controls overflowed the
// frame across 700-840 (the mobile-catastrophe screenshot, 2026-07-17).
// 840 = measured need + margin (fine pointer). Coarse pointer inflates
// every control to its 44px touch floor, so the same row measures
// ~910px — a landscape phone at ~890 sheared the theme toggle through
// the card edge (operator screenshot). The ladder resolves the pointer
// class per application and uses the wider thresholds under touch.
const HEADER_COLLAPSE_METRIC_PX = 840;
const HEADER_COLLAPSE_METRIC_COARSE_PX = 940;
// Host width below which the answer band wraps two cells per row. Lives on
// the collapse ladder, not in a media query: a media query in a shadow root
// measures the viewport, so an embed narrowed inside a wide page never fired
// it. Same reason every other rung reads the ResizeObserver's contentRect.
const BAND_WRAP_PX = 560;
// Chart geometry tier threshold: 700 is where the base view's four-item
// legend (~630px measured) stops fitting one wide-mode row, and it is
// the historical "desktop unchanged at or above" line — chart option
// geometry never differs from the settled desktop at >= 700.
const CHART_COMPACT_PX = 700;
// Header wrap rung: with the meta hidden, the title + control cluster
// single row measures ~590px, so below 620 (measured + margin) the
// header trades its fixed row for wrapping (the embedded explorer
// header's existing behavior). Below 430 the band's headline cell goes
// full-width — the five cells pack [headline] [2,3] [4,5] instead of
// leaving cell 5 orphaned — and the frame padding tightens.
const HEADER_WRAP_PX = 620;
const HEADER_WRAP_COARSE_PX = 700;
const PHONE_PX = 430;
// Below the supported 320px floor (foldable covers, oddball webviews)
// the band stacks one cell per row — the documented exception to the
// no-sprawl law: beneath the smallest mainstream width, legibility
// outranks density.
const BAND_STACK_PX = 320;
const GATED_CAPTION =
  "Hatched epochs: cluster statistics withheld below 50% coverage";
// The terminus dot's prose half: the tooltip answers "why" on hover, this
// answers it for a reader who never hovers. Distinct from GATED_CAPTION —
// a measurement not yet made is not one withheld for thin coverage.
const PENDING_CAPTION =
  "Cluster lines end at the dot: an epoch's distribution is measured during the following epoch. Our own yield is measured and plotted throughout.";

// Chart-host keyboard contract, re-set per render: the N key exists only
// when the base view actually carries the legend-deselected net series.
const CHART_KEYS_LABEL = "Epoch chart: Left/Right selects an epoch, Enter pins it";
const CHART_KEYS_LABEL_WITH_NET = `${CHART_KEYS_LABEL}, N toggles the net incl MEV series`;

// Band-tab accessible DESCRIPTION (not name): the statistic text stays the
// accessible name; this names what activation does (aria-description is
// additive — an aria-label would hide the measured value from AT users).
// The "income" entry describes the header [validator income] toggle (its
// view is header-triggered, not a band tab).
const VIEW_DESCRIPTIONS: Record<ChartView, string> = {
  base: "switches the plot below to the base APY chart",
  position: "switches the plot below to the cluster-position history",
  mev: "switches the plot below to the MEV component per epoch",
  income: "switches the plot below to the validator's per-epoch net income line in SOL",
};

// Theme-toggle glyphs: inline SVG (hairline stroke, currentColor), never
// emoji. The glyph shows the theme a click switches TO.
const SUN_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
  '<circle cx="8" cy="8" r="3.4" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
  '<path d="M8 0.8v1.9M8 13.3v1.9M0.8 8h1.9M13.3 8h1.9M2.9 2.9l1.35 1.35M11.75 11.75l1.35 1.35' +
  'M13.1 2.9l-1.35 1.35M4.25 11.75L2.9 13.1" fill="none" stroke="currentColor" ' +
  'stroke-width="1.2" stroke-linecap="round"/></svg>';
const MOON_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
  '<path d="M13.4 9.7A5.9 5.9 0 0 1 6.3 2.6a5.9 5.9 0 1 0 7.1 7.1Z" fill="none" ' +
  'stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>';
// Trailing window of the cluster-position cell, in epochs: ~one quarter at
// ~2-day epochs. Overridable per instance via the position-window attribute.
const DEFAULT_POSITION_WINDOW = 45;

// Trailing window (real days) for the ENTIRE answer band -- the headline APY,
// the cluster deltas, and the cluster-position cell all stand on this one
// window, expressed in days (institutions read days, not Solana epochs). A
// single epoch's annualized APY carries a duration artifact (a long/short
// epoch mis-states its rate); the band is instead the yield realized over this
// many days, annualized -- the SEC 30-day-yield construction. 30 days is long
// enough to defeat "you annualized a few days" and current enough to not
// overstate forward yield via disinflation. One constant governs the whole
// band: retune here. (The position TAB and the plot's default zoom keep their
// own epoch-count viewport via #positionWindow; that is a viewport, not a
// claimed metric window.)
const BAND_TRAILING_DAYS = 30;

// Max epochs the net/MEV cell may lag the base headline before it stops showing
// a stale settled value and states "pending" instead. MEV settles ~1 epoch
// behind inflation normally; beyond this a settlement gap is real and an old
// figure beside a current headline would read as current (audit HIGH).
const MEV_MAX_LAG_EPOCHS = 2;

/** Narrows a nullable series element to a present number (excludes null/undefined). */
function present(x: number | null | undefined): x is number {
  return x !== null && x !== undefined;
}

function parseEpochAttr(value: string | null, name: string): number | undefined {
  if (value === null || value.trim() === "") {
    return undefined;
  }
  const n = Number(value);
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new ApiError(`attribute ${name}: expected a non-negative integer, got "${value}"`);
  }
  return n;
}

function parsePositionWindowAttr(value: string | null): number {
  if (value === null || value.trim() === "") {
    return DEFAULT_POSITION_WINDOW;
  }
  const n = Number(value);
  if (!Number.isSafeInteger(n) || n < 1) {
    throw new ApiError(`attribute position-window: expected a positive integer, got "${value}"`);
  }
  return n;
}

export class VfApyChart extends HTMLElement {
  static readonly observedAttributes = [
    "api-base",
    "from-epoch",
    "to-epoch",
    "position-window",
    "theme",
  ];

  #root: HTMLDivElement | null = null;
  #shadow: ShadowRoot | null = null;
  #built = false;

  #statusEl: HTMLDivElement | null = null;
  #errorEl: HTMLDivElement | null = null;
  #headerEl: HTMLDivElement | null = null;
  #bandEl: HTMLDivElement | null = null;
  // Compact live row's ETA text ("next block ≈ Nm" / amber stale age /
  // "no further leader slots this epoch"), right of the leader track. Its
  // data plane is independent of #load: a chart reload never blanks it,
  // and its poll failures never touch the chart's error state.
  #liveEtaEl: HTMLDivElement | null = null;
  #liveTimer: ReturnType<typeof setInterval> | null = null;
  #liveTickCount = 0;
  #liveAbort: AbortController | null = null;
  #liveFacts: LiveFacts | null = null;
  // True once a poll has failed with nothing rendered yet: the strip then
  // says "live data unavailable" (muted) instead of staying blank.
  #liveFailedOnce = false;
  // Live region wrapper (strip + leader track): the activatable trigger of
  // the blocks datastream panel. Hidden until the first live render and
  // while the explorer covers the surfaces.
  #liveRegionEl: HTMLDivElement | null = null;
  #liveTrackEl: HTMLDivElement | null = null;
  #liveRendered = false;
  // The current epoch's served leader timeline (/schedule?epoch): the
  // track's tick set and the stream table's fee/time source. Epoch-tagged;
  // an epoch roll drops it (bare track, no stale ticks) until the new
  // timeline lands.
  #liveSlots: {
    epoch: number;
    startSlot: number;
    slotsInEpoch: number;
    slots: ScheduleSlot[];
  } | null = null;
  // Blocks datastream panel (below the plot, drilldown zone) and its two
  // joined sources, each epoch-tagged so a roll can never re-label old
  // facts. Fetched on panel open and on the shared poll while open — its
  // own abort plane, independent of both the chart load and the live poll.
  #stream: BlocksStreamPanel | null = null;
  #streamHostEl: HTMLDivElement | null = null;
  #streamAbort: AbortController | null = null;
  #streamSkips: { epoch: number; row: SkipsRow | null } | null = null;
  #streamTips: { epoch: number; tipsBySlot: Map<number, number> } | null = null;

  #chartWrapEl: HTMLDivElement | null = null;
  #chartHost: HTMLDivElement | null = null;
  #viewFootnoteEl: HTMLDivElement | null = null;
  #gapCaptionEl: HTMLDivElement | null = null;
  #freshnessEl: HTMLDivElement | null = null;
  #pinLaneEl: HTMLDivElement | null = null;
  #footerEl: HTMLDivElement | null = null;
  #drilldownHost: HTMLDivElement | null = null;
  // Polite live region: keyboard epoch moves and the net-series key have
  // no accessible output otherwise (the transient tooltip is visual-only).
  #liveEl: HTMLDivElement | null = null;

  #chart: EChartsType | null = null;
  #legendSelected: Record<string, boolean> = {};
  #pins: PinLayer | null = null;
  #drilldown: DrilldownPanel | null = null;
  #resizeObserver: ResizeObserver | null = null;
  #widthObserver: ResizeObserver | null = null;
  // Chart geometry tier, set by the collapse ladder from the same host
  // width as the CSS rungs. "wide" until a real measurement says
  // otherwise (width 0 = unmeasured, e.g. before first layout), so the
  // SSR/test paths stay on the settled desktop geometry.
  #chartTier: ChartLayoutTier = "wide";
  // Capability listeners: the pointer class can flip WITHOUT a resize (a
  // 2-in-1 detaching its keyboard) and the OS scheme can flip mid-session
  // (auto theme, no attribute). The CSS media blocks update live on their
  // own; these keep the JS-side rungs and the resolved theme in step.
  #coarseMql: MediaQueryList | null = null;
  #schemeMql: MediaQueryList | null = null;
  readonly #onPointerClassChange = (): void => {
    this.#applyCollapseLadder(this.clientWidth);
    if (this.#assembled !== null) {
      this.#renderChart();
    }
  };
  readonly #onSchemeChange = (): void => {
    if (this.getAttribute("theme") !== null) {
      return;
    }
    this.#applyThemeClass();
    this.#explorer?.setAttribute("theme", this.#theme.name);
    if (this.#assembled !== null) {
      this.#render();
    }
  };
  // Tap-away tooltip dismissal: on touch no mouseleave ever fires, so
  // the axis tooltip stayed on screen covering the plot (operator,
  // real-device 2026-07-17). Any pointer-down outside the plot hides
  // it; on desktop this is a no-op alongside the existing mouseleave.
  readonly #onDocPointerDownHideTip = (ev: PointerEvent): void => {
    if (this.#chart === null || this.#chartHost === null) {
      return;
    }
    if (ev.composedPath().includes(this.#chartHost)) {
      return;
    }
    this.#chart.dispatchAction({ type: "hideTip" });
  };
  #abort: AbortController | null = null;
  #seq = 0;
  #assembled: Assembled | null = null;
  #fetchedAt: Date | null = null;
  // Embedded income explorer (base mode): created on FIRST
  // toggle only, so embedding costs zero fetches until an engineer opens
  // it. While open, every chart surface hides beneath it; the header stays.
  #explorer: HTMLElement | null = null;
  #explorerOpen = false;
  #clickBound = false;
  // Keyboard tooltip target: index of the rendered view's subject series
  // (the hero line in the base view; the single series elsewhere). Never
  // "last series" — in base mode the last series is the legend-deselected
  // net line, and showTip aimed at a hidden series is undefined behavior.
  #tipSeriesIndex = 0;
  // True while the rendered base view carries the legend-deselected net
  // series — the precondition for the N legend-toggle key.
  #netToggleAvailable = false;
  // Arrow-key epoch cursor. Starts at the headline epoch (the newest
  // covered one — where the read starts) and resets with the data, so a
  // reload onto a shorter epoch spine never leaves a stale over-range
  // index that turns the keys into silent no-ops.
  #keyboardCursor = 0;
  // View state (answer-band tabs + the header income toggle): which plot
  // renders below the band, and the user's live dataZoom window — carried
  // across re-renders and view switches so the slider never resets under
  // them.
  #view: ChartView = "base";
  // null = the reader has not touched the slider, so each view applies its
  // OWN default: the full chart opens on the position window (the epochs the
  // band's claim is measured over), the income view on its served-data span.
  // Explicitly null rather than sniffing for {0,100}: that value is the
  // income view's own untouched sentinel, and once the base view defaults to
  // a partial window, a shared sentinel cannot tell "untouched" from "the
  // other view's default".
  #zoom: ZoomWindow | null = null;
  // View to restore when the [validator income] toggle is pressed a second
  // time (C51: "returns to the previous chart view").
  #viewBeforeIncome: ChartView = "base";
  // Epoch pinned by a click in the income view: drives the amber mark line
  // and tracks the drill-down panel it opened. Cleared when the panel
  // hides (visibility callback) and on any exit from the income view.
  #incomePinnedEpoch: number | null = null;
  // Income-view unit (SOL net line / block-fees USD at receipt). A field,
  // not an attribute: the choice persists across view switches and theme
  // re-renders for the widget's lifetime, and resets to SOL with the data
  // (#clearRenderedData) — a new response may not carry the USD fields.
  #incomeUnit: IncomeUnit = "sol";
  // Enabled SOL-mode channel lines. A field like the unit: persists across
  // view switches for the widget's lifetime, resets to net-only with the
  // data (#clearRenderedData). Invariant: never empty — the toggle handler
  // ignores a press that would switch off the last visible line.
  // All channels on by default (operator 2026-07-14: the full picture IS
  // the point of this view); reset restores the same full set.
  #incomeChannels: Set<IncomeChannel> = new Set(["net", "fees", "tips", "bam"]);
  #unitSwitchEl: HTMLDivElement | null = null;
  #channelSwitchEl: HTMLDivElement | null = null;
  #incomeControlsEl: HTMLDivElement | null = null;
  // [apy|position|mev] segmented control at the plot's top-right: the
  // VISIBLE counterpart of the answer-band view tabs, which stay clickable
  // as the power-user shortcut. Base mode only; never inside the income
  // view (the SOL|USD unit switch owns that corner) or under the explorer.
  #viewSwitchEl: HTMLDivElement | null = null;
  // Default zoom viewport, in epochs: the plot opens on the last W epochs
  // (position-window attribute; validated in #load alongside from-epoch/
  // to-epoch). A viewport only — the answer band's own window is day-anchored
  // (BAND_TRAILING_DAYS) and independent of this.
  #positionWindow = DEFAULT_POSITION_WINDOW;

  connectedCallback(): void {
    this.#renderSkeleton();
    this.#applyThemeClass();
    if (typeof matchMedia === "function") {
      this.#coarseMql = matchMedia("(pointer: coarse)");
      this.#coarseMql.addEventListener?.("change", this.#onPointerClassChange);
      this.#schemeMql = matchMedia("(prefers-color-scheme: dark)");
      this.#schemeMql.addEventListener?.("change", this.#onSchemeChange);
    }
    document.addEventListener("pointerdown", this.#onDocPointerDownHideTip, true);
    void this.#load();
    this.#startLive();
  }

  disconnectedCallback(): void {
    this.#seq += 1;
    this.#abort?.abort();
    this.#abort = null;
    if (this.#liveTimer !== null) {
      clearInterval(this.#liveTimer);
      this.#liveTimer = null;
    }
    this.#liveAbort?.abort();
    this.#liveAbort = null;
    this.#liveFacts = null;
    this.#liveFailedOnce = false;
    this.#liveRendered = false;
    this.#liveSlots = null;
    this.#streamAbort?.abort();
    this.#streamAbort = null;
    this.#streamSkips = null;
    this.#streamTips = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#widthObserver?.disconnect();
    this.#widthObserver = null;
    this.#coarseMql?.removeEventListener?.("change", this.#onPointerClassChange);
    this.#coarseMql = null;
    this.#schemeMql?.removeEventListener?.("change", this.#onSchemeChange);
    this.#schemeMql = null;
    document.removeEventListener("pointerdown", this.#onDocPointerDownHideTip, true);
    this.#chart?.dispose();
    this.#chart = null;
    this.#pins?.destroy();
    this.#pins = null;
    // The explorer's own disconnectedCallback aborts its in-flight fetches
    // and disposes its chart; removing it here guarantees disposal even if
    // the host is later reattached with a rebuilt skeleton.
    this.#explorer?.remove();
    this.#explorer = null;
    this.#explorerOpen = false;
    this.#clickBound = false;
    this.#built = false;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || this.#root === null) {
      return;
    }
    if (name === "theme") {
      this.#applyThemeClass();
      // Mirror onto the embedded explorer so it re-themes without a
      // refetch. The RESOLVED name is mirrored (not the raw attribute):
      // with the attribute absent this element resolves through
      // prefers-color-scheme, which the explorer does not — an unresolved
      // mirror could leave the two halves on different themes.
      this.#explorer?.setAttribute("theme", this.#theme.name);
      if (this.#assembled !== null) {
        this.#render();
      }
      return;
    }
    void this.#load();
    // The live strip reads api-base per poll; re-poll now rather than
    // waiting out the current 60 s cadence. The live plane's caches drop
    // with the (possibly changed) data source — the next poll refills
    // them; an api-base switch must never keep serving the old origin's
    // timeline, quality row, or tips as the new one's.
    this.#liveSlots = null;
    this.#streamSkips = null;
    this.#streamTips = null;
    void this.#pollLive();
  }

  get #theme(): ThemeTokens {
    return themeTokens(this.#effectiveThemeName());
  }

  /**
   * Explicit `theme` attribute when present; otherwise the embedding
   * page's prefers-color-scheme — the in-widget theme toggle needs a true
   * starting state on embeds that never set the attribute.
   */
  #effectiveThemeName(): "light" | "dark" {
    const attr = this.getAttribute("theme");
    if (attr === "light" || attr === "dark") {
      return attr;
    }
    const prefersDark =
      typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
  }

  // --- basis selection (operator mode rule: base primary once the API
  // serves delegator_apy_infl_* columns; net-only rendering, byte-identical
  // to pre-0.7.0, otherwise) --------------------------------------------

  #basisOf(a: Assembled | null): StatsBasis {
    return a?.inflationKeysPresent === true ? "base" : "net";
  }

  /**
   * Whether the net-incl-MEV line is currently plotted. Hidden by default
   * (the rendered option deselects it), revealed via the legend or the N key.
   * Gates the tooltip/pin/copy NET INCL MEV group so its base-band
   * comparability caveat appears only while the line it warns about is on
   * the chart.
   */
  #netRevealed(): boolean {
    return this.#legendSelected[SERIES_OUR_NET_DASHED] === true;
  }

  #headlineFor(a: Assembled | null): HeadlineEpoch | null {
    if (a === null) {
      return null;
    }
    return a.inflationKeysPresent ? selectHeadlineEpochBase(a) : selectHeadlineEpoch(a);
  }

  #hasCoveredFor(a: Assembled | null): boolean {
    if (a === null) {
      return false;
    }
    return a.inflationKeysPresent ? hasCoveredEpochBase(a) : hasCoveredEpoch(a);
  }

  // --- skeleton -----------------------------------------------------------

  #renderSkeleton(): void {
    if (this.#shadow === null) {
      this.#shadow = this.attachShadow({ mode: "open" });
      const style = document.createElement("style");
      style.textContent = STYLES;
      this.#shadow.append(style);
    }
    if (this.#built) {
      return;
    }
    this.#built = true;

    this.#chart?.dispose();
    this.#chart = null;
    this.#pins?.destroy();
    this.#pins = null;
    this.#clickBound = false;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#explorer?.remove();
    this.#explorer = null;
    this.#explorerOpen = false;
    this.#view = "base";
    this.#viewBeforeIncome = "base";
    this.#incomePinnedEpoch = null;
    this.#incomeUnit = "sol";
    this.#zoom = null;

    // Remove every previous node except the <style> tag.
    for (const node of [...(this.#shadow.childNodes as unknown as ChildNode[])]) {
      if (!(node instanceof HTMLStyleElement)) {
        node.remove();
      }
    }

    const root = document.createElement("div");
    root.setAttribute("role", "figure");
    root.className = "vf-root";

    // Live-region roles, set once at skeleton build: subsequent
    // textContent swaps announce automatically. Loading and the error
    // state (the widget's honesty centerpiece) were silent to AT.
    const errorEl = document.createElement("div");
    errorEl.className = "vf-error vf-hidden";
    errorEl.setAttribute("role", "alert");
    const statusEl = document.createElement("div");
    statusEl.className = "vf-status vf-hidden";
    statusEl.setAttribute("role", "status");

    const headerEl = document.createElement("div");
    headerEl.className = "vf-header";
    const bandEl = document.createElement("div");
    bandEl.className = "vf-band";
    // Live region (phase 3, operator redesign): ONE compact row — the
    // leader progress track (flex-grow, the dominant element) with the
    // ETA text to its right. The verbose sentence moved into the blocks
    // datastream panel this region expands. role=button on the wrapper;
    // the aria-label names the region and what activation does (the
    // content no longer carries a self-describing sentence). Hidden until
    // the first live render fills it.
    const liveRegionEl = document.createElement("div");
    liveRegionEl.className = "vf-live-region vf-hidden";
    liveRegionEl.setAttribute("role", "button");
    liveRegionEl.tabIndex = 0;
    liveRegionEl.setAttribute("aria-expanded", "false");
    liveRegionEl.setAttribute(
      "aria-label",
      "epoch leader schedule: activate for the live blocks stream",
    );
    liveRegionEl.addEventListener("click", () => this.#toggleStream());
    liveRegionEl.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        this.#toggleStream();
      }
    });
    // Leader progress track: aria-hidden — the panel carries the facts.
    const liveTrackEl = document.createElement("div");
    liveTrackEl.className = "vf-live-track";
    liveTrackEl.setAttribute("aria-hidden", "true");
    // ETA text (or amber stale age — staleness stays visible in the
    // compact row, never behind a click). Plain div on purpose: a
    // role=status live region would announce every 30 s countdown tick.
    const liveEtaEl = document.createElement("div");
    liveEtaEl.className = "vf-live-eta";
    liveRegionEl.append(liveTrackEl, liveEtaEl);

    root.append(errorEl, statusEl, headerEl, bandEl, liveRegionEl);
    this.#root = root;
    this.#errorEl = errorEl;
    this.#statusEl = statusEl;
    this.#headerEl = headerEl;
    this.#bandEl = bandEl;
    this.#liveEtaEl = liveEtaEl;
    this.#liveTrackEl = liveTrackEl;
    this.#liveRegionEl = liveRegionEl;

    const chartWrap = document.createElement("div");
    chartWrap.className = "vf-chart-wrap";
    const chartHost = document.createElement("div");
    chartHost.className = "vf-chart";
    chartHost.tabIndex = 0;
    chartHost.setAttribute("role", "application");
    chartHost.setAttribute("aria-label", CHART_KEYS_LABEL);
    chartWrap.append(chartHost);
    // SOL|USD unit switch (income view only, and only when the /income
    // rows carry the USD valuation fields): overlays the plot's top-right
    // margin — the grid's 40px top band is chrome-only space, so the
    // control never occludes data ink and adds no layout shift.
    // Income-view corner controls: unit switch + channel toggles + [x]
    // close, one overlay container at the plot top-right. The income
    // view's own legend (2+ visible lines) anchors top-LEFT, so the
    // right corner stays free for these controls — unlike the base view,
    // whose top-right belongs to its legend.
    const incomeControls = document.createElement("div");
    incomeControls.className = "vf-income-controls vf-hidden";
    const unitSwitch = document.createElement("div");
    unitSwitch.className = "vf-unit-switch vf-hidden";
    unitSwitch.setAttribute("role", "group");
    unitSwitch.setAttribute("aria-label", "income view unit");
    // [net][fees][tips][bam] additive channel toggles, SOL mode only:
    // multi-select segments (each toggles its line), between the unit
    // switch and the [x].
    const channelSwitch = document.createElement("div");
    channelSwitch.className = "vf-channel-switch vf-hidden";
    channelSwitch.setAttribute("role", "group");
    channelSwitch.setAttribute("aria-label", "income channels");
    const incomeClose = document.createElement("button");
    incomeClose.type = "button";
    incomeClose.className = "vf-btn";
    incomeClose.classList.add("vf-close");
    incomeClose.append(closeIcon());
    incomeClose.setAttribute("aria-label", "close validator income view");
    incomeClose.addEventListener("click", () => this.#toggleIncomeView());
    incomeControls.append(unitSwitch, channelSwitch, incomeClose);
    // BEFORE the chart host in DOM: as an absolute overlay (wide) the
    // order is invisible, but the chart-compact rung turns this into a
    // static in-flow row that must sit ABOVE the plot in reading
    // direction — an overlay row of 44px coarse-pointer buttons at
    // phone width blankets the legend underneath it.
    chartWrap.prepend(incomeControls);
    // apy|position|mev view switch (base mode, main chart views only):
    // segment grammar shared with the unit switch, but it lives in the
    // HEADER controls row, not the plot corner — the base view's legend
    // owns the plot's top margin, and an overlay there collides with it
    // at narrow widths (operator screenshot, 2026-07-14). The element is
    // created here and adopted by #renderHeaderFull on every render.
    const viewSwitch = document.createElement("div");
    viewSwitch.className = "vf-view-switch vf-hidden";
    viewSwitch.setAttribute("role", "group");
    viewSwitch.setAttribute("aria-label", "chart view");
    // View-specific derivation/absence footnote (position, mev, and the
    // income view's USD mode), directly under the plot it describes.
    const viewFootnoteEl = document.createElement("div");
    viewFootnoteEl.className = "vf-caption vf-view-footnote vf-hidden";
    const gapCaptionEl = document.createElement("div");
    gapCaptionEl.className = "vf-caption vf-hidden";
    // Freshness line: the header meta's "through epoch N · ended ..."
    // fact, rendered below the plot but DISPLAYED only while the
    // vf-collapsed-metric rung hides the header meta (CSS-gated via
    // .vf-meta-collapsed on the root) — hidden content stays reachable,
    // and the toggle is pure CSS so ladder crossings need no re-render.
    const freshnessEl = document.createElement("div");
    freshnessEl.className = "vf-caption vf-freshness vf-mono";
    const pinLaneEl = document.createElement("div");
    pinLaneEl.className = "vf-pin-lane";
    const drilldownHost = document.createElement("div");
    drilldownHost.className = "vf-hidden";
    // Blocks datastream host: the second below-the-plot detail zone. The
    // panel is a full exclusivity participant (phase 3): it and the
    // drill-down, income view, and explorer close each other — see
    // #toggleStream / #showDrilldown / #closeOtherSurfaces.
    const streamHost = document.createElement("div");
    streamHost.className = "vf-hidden";
    const footerEl = document.createElement("div");
    footerEl.className = "vf-footer vf-hidden";
    const liveEl = document.createElement("div");
    liveEl.className = "vf-visually-hidden";
    liveEl.setAttribute("aria-live", "polite");

    // The income drill-down panel anchors BELOW the plot, after the pin
    // lane (operator direction, C51 round): every click-expanded detail
    // lives in the same below-the-plot zone as the pin card, for ALL its
    // openers (income-view click, pin-card details).
    root.append(
      chartWrap,
      viewFootnoteEl,
      gapCaptionEl,
      freshnessEl,
      pinLaneEl,
      drilldownHost,
      streamHost,
      footerEl,
      liveEl,
    );

    this.#chartWrapEl = chartWrap;
    this.#chartHost = chartHost;
    this.#unitSwitchEl = unitSwitch;
    this.#channelSwitchEl = channelSwitch;
    this.#incomeControlsEl = incomeControls;
    this.#viewSwitchEl = viewSwitch;
    this.#viewFootnoteEl = viewFootnoteEl;
    this.#gapCaptionEl = gapCaptionEl;
    this.#freshnessEl = freshnessEl;
    this.#pinLaneEl = pinLaneEl;
    this.#footerEl = footerEl;
    this.#liveEl = liveEl;
    this.#drilldownHost = drilldownHost;
    this.#streamHostEl = streamHost;
    // Visibility callback keeps the trigger's aria-expanded true exactly
    // while the panel is open, on every close path ([x], Esc, exclusivity).
    this.#stream = new BlocksStreamPanel(
      streamHost,
      { onBlockSelect: (epoch, slot) => this.#openExplorerAtBlock(epoch, slot) },
      (open) => {
        this.#liveRegionEl?.setAttribute("aria-expanded", String(open));
      },
    );
    // Visibility callback ties the income view's clicked-epoch mark to the
    // panel it opened: hiding the panel on ANY path ([x], Esc, replace)
    // clears the amber mark line instead of leaving a stale pin.
    this.#drilldown = new DrilldownPanel(drilldownHost, (open) => {
      if (!open && this.#incomePinnedEpoch !== null) {
        this.#incomePinnedEpoch = null;
        if (this.#view === "income") {
          this.#renderChart();
        }
      }
    });
    this.#pins = new PinLayer(pinLaneEl, {
      plotHost: chartHost,
      onChange: () => this.#renderChart(),
      onDismissFocus: () => chartHost.focus({ preventScroll: true }),
    });

    chartHost.addEventListener("keydown", (ev) => this.#onChartKeydown(ev));

    this.#resizeObserver = new ResizeObserver(() => this.#chart?.resize());
    this.#resizeObserver.observe(chartHost);

    this.#shadow.append(root);

    this.#widthObserver?.disconnect();
    this.#widthObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      this.#applyCollapseLadder(width);
    });
    this.#widthObserver.observe(this);
  }

  // The collapse ladder, top rung to bottom: below 840 the header meta
  // (and its separator) hide, and the same fact surfaces in the footer
  // freshness line (hidden content stays reachable); below 700 the
  // chart drops to its compact geometry tier; below 620 the header
  // control cluster wraps to a second row; below 560 the band wraps two
  // cells per row; below 430 the band headline cell goes full-width and
  // the frame padding tightens. Every rung keys off the HOST width
  // (never a media query — a shadow-root media query measures the
  // viewport and misses an embed narrowed inside a wide page).
  #applyCollapseLadder(width: number): void {
    // Header rungs are pointer-aware: coarse targets widen the control
    // row, so its thresholds shift up. Chart/band rungs stay pure width.
    const coarse = this.#coarsePointer();
    const metricPx = coarse ? HEADER_COLLAPSE_METRIC_COARSE_PX : HEADER_COLLAPSE_METRIC_PX;
    const wrapPx = coarse ? HEADER_WRAP_COARSE_PX : HEADER_WRAP_PX;
    this.#headerEl?.classList.toggle("vf-collapsed-metric", width < metricPx);
    this.#headerEl?.classList.toggle("vf-header-wrap", width < wrapPx);
    this.#bandEl?.classList.toggle("vf-band-narrow", width < BAND_WRAP_PX);
    this.#bandEl?.classList.toggle("vf-band-phone", width < PHONE_PX);
    this.#bandEl?.classList.toggle("vf-band-stack", width > 0 && width < BAND_STACK_PX);
    this.#root?.classList.toggle("vf-meta-collapsed", width < metricPx);
    this.#root?.classList.toggle("vf-chart-compact", width < CHART_COMPACT_PX);
    this.#root?.classList.toggle("vf-compact", width < wrapPx);
    this.#root?.classList.toggle("vf-phone", width < PHONE_PX);

    // Chart geometry is ECharts option values, not CSS, so the tier
    // re-renders the option — only on an actual crossing (never per
    // resize pixel; in-tier width changes stay on the cheap
    // chart.resize() path of the other observer). Width 0 = unmeasured:
    // stay wide rather than flash compact geometry before first layout.
    const tier: ChartLayoutTier = width > 0 && width < CHART_COMPACT_PX ? "compact" : "wide";
    if (tier !== this.#chartTier) {
      this.#chartTier = tier;
      if (this.#chart !== null) {
        this.#renderChart();
      }
    }
  }

  /** Coarse primary pointer (touch). Read per render, not cached: cheap, and a tablet attaching a mouse re-resolves on the next render. */
  #coarsePointer(): boolean {
    return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  }

  /** The layout every chart option builder receives; wide/fine reproduces the settled desktop geometry exactly. */
  #chartLayout(): ChartLayout {
    return { tier: this.#chartTier, coarse: this.#coarsePointer() };
  }

  #applyThemeClass(): void {
    this.#root?.classList.toggle("dark", this.#theme.name === "dark");
  }

  #showStatus(text: string | null): void {
    if (this.#statusEl === null) {
      return;
    }
    this.#statusEl.textContent = text ?? "";
    this.#statusEl.classList.toggle("vf-hidden", text === null);
  }

  #showError(message: string | null): void {
    if (this.#errorEl === null) {
      return;
    }
    this.#errorEl.replaceChildren();
    if (message !== null) {
      const title = document.createElement("div");
      title.className = "vf-error-title";
      title.textContent = "vf-apy-chart: data unavailable";
      const body = document.createElement("div");
      body.textContent = message;
      // In-widget recovery for transient failures, mirroring the
      // explorer's error-state pattern (same control vocabulary and
      // placement). The verbatim reason above stays untouched.
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "vf-btn vf-retry";
      retry.textContent = "retry";
      retry.addEventListener("click", () => {
        void this.#load();
      });
      this.#errorEl.append(title, body, retry);
    }
    this.#errorEl.classList.toggle("vf-hidden", message === null);
  }

  /** Feeds the polite live region (keyboard epoch moves, net-series toggle). */
  #announce(text: string): void {
    if (this.#liveEl !== null) {
      this.#liveEl.textContent = text;
    }
  }

  // --- data load ------------------------------------------------------------

  /** Blank every rendered surface so a refetch never leaves stale old-API values visible during load or after an error. */
  #clearRenderedData(): void {
    this.#assembled = null;
    this.#keyboardCursor = 0;
    // New data, new default view and full-range slider window: neither
    // state is meaningful against a different epoch spine (and the new
    // response may not serve the alternate views' columns at all).
    this.#view = "base";
    this.#viewBeforeIncome = "base";
    this.#incomePinnedEpoch = null;
    // Unit and channel toggles reset WITH the data (spec): the next
    // response may not carry the USD fields, and net-only is the view's
    // first-paint answer against any new epoch spine.
    this.#incomeUnit = "sol";
    this.#incomeChannels = new Set(["net", "fees", "tips", "bam"]);
    this.#zoom = null;
    if (this.#unitSwitchEl !== null) {
      this.#unitSwitchEl.replaceChildren();
      this.#unitSwitchEl.classList.add("vf-hidden");
    }
    if (this.#channelSwitchEl !== null) {
      this.#channelSwitchEl.replaceChildren();
      this.#channelSwitchEl.classList.add("vf-hidden");
    }
    this.#incomeControlsEl?.classList.add("vf-hidden");
    if (this.#viewSwitchEl !== null) {
      this.#viewSwitchEl.replaceChildren();
      this.#viewSwitchEl.classList.add("vf-hidden");
    }
    if (this.#viewFootnoteEl !== null) {
      this.#viewFootnoteEl.textContent = "";
      this.#viewFootnoteEl.classList.add("vf-hidden");
    }
    this.#pins?.destroy();
    this.#drilldown?.hide();
    this.#headerEl?.replaceChildren();
    this.#bandEl?.replaceChildren();
    this.#chart?.clear();
    // The embedded explorer is torn down with the data it was created
    // against: a source/basis reload must never leave it rendering the
    // prior source's rows, and a reload into net-only mode (no explorer
    // button) must not let a stale #explorerOpen keep the main surfaces
    // hidden with no visible way back.
    this.#explorer?.remove();
    this.#explorer = null;
    this.#explorerOpen = false;
    if (this.#footerEl !== null) {
      this.#footerEl.classList.add("vf-hidden");
    }
    if (this.#gapCaptionEl !== null) {
      this.#gapCaptionEl.classList.add("vf-hidden");
    }
    // Text, not only visibility: the freshness line duplicates the header
    // meta's epoch claim, and a cleared instrument must hold no stale
    // figure anywhere in its DOM (the refetch-failure invariant).
    if (this.#freshnessEl !== null) {
      this.#freshnessEl.textContent = "";
    }
    this.#root?.removeAttribute("aria-label");
  }

  async #load(): Promise<void> {
    const seq = ++this.#seq;
    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;

    this.#clearRenderedData();
    this.#showError(null);
    this.#showStatus("Loading epoch data...");

    try {
      const base = this.getAttribute("api-base");
      if (base === null || base.trim() === "") {
        throw new ApiError('attribute api-base is required (e.g. "https://example.com")');
      }
      const fromEpoch = parseEpochAttr(this.getAttribute("from-epoch"), "from-epoch");
      const toEpoch = parseEpochAttr(this.getAttribute("to-epoch"), "to-epoch");
      this.#positionWindow = parsePositionWindowAttr(this.getAttribute("position-window"));

      const client = createClient(base.trim());
      const [apy, cluster, income, rank] = await Promise.all([
        client.fetchApy(abort.signal),
        client.fetchCluster(fromEpoch, toEpoch, abort.signal),
        client.fetchIncome(abort.signal),
        client.fetchRank(abort.signal),
      ]);
      if (seq !== this.#seq) {
        return;
      }

      this.#assembled = assemble(apy, cluster, income, fromEpoch, toEpoch, rank);
      this.#fetchedAt = new Date();
      this.#resetKeyboardCursor();
      if (this.#assembled === null) {
        this.#showStatus(null);
        this.#render();
        return;
      }
      this.#showStatus(null);
      this.#render();
    } catch (e) {
      if (seq !== this.#seq) {
        return;
      }
      this.#showStatus(null);
      this.#showError(e instanceof Error ? e.message : String(e));
    }
  }

  #render(): void {
    this.#renderFull();
  }

  // --- live epoch strip ------------------------------------------------------

  /**
   * One timer drives the strip: every LIVE_TICK_MS (30 s) it either
   * re-renders the countdown from the poll-time anchor (odd ticks) or
   * polls both endpoints (even ticks — the 60 s cadence the contract
   * recipe asks for). The first poll fires immediately on connect.
   */
  #startLive(): void {
    if (this.#liveTimer !== null) {
      clearInterval(this.#liveTimer);
    }
    this.#liveTickCount = 0;
    void this.#pollLive();
    this.#liveTimer = setInterval(() => {
      this.#liveTickCount += 1;
      if (this.#liveTickCount % 2 === 0) {
        void this.#pollLive();
      } else {
        this.#renderLive();
      }
    }, LIVE_TICK_MS);
  }

  /**
   * Polls /schedule (with the current epoch's timeline), then /summary
   * over the running epoch's slot range. The epoch bounds derive from the
   * served head (observed_slot − slot_index .. + slots_in_epoch − 1):
   * measured values, equal to epoch × 432000 on mainnet's warmup-free
   * schedule. Any failure keeps the last rendered facts — staleness
   * accrues via the anchor instead of the line blanking (ambient chrome,
   * no error panel).
   *
   * Timeline law: the served `slots` are accepted only when the epoch
   * they were REQUESTED for is the served head's epoch. The first poll
   * (epoch unknown) and an epoch roll therefore chain one follow-up
   * request for the head epoch's timeline; if that follow-up fails, the
   * track renders bare (cursor only) — never stale ticks.
   */
  async #pollLive(): Promise<void> {
    this.#liveAbort?.abort();
    const abort = new AbortController();
    this.#liveAbort = abort;
    try {
      const base = this.getAttribute("api-base");
      if (base === null || base.trim() === "") {
        throw new ApiError("attribute api-base is required");
      }
      const client = createClient(base.trim());
      const knownEpoch =
        this.#liveSlots?.epoch ?? this.#liveFacts?.epoch;
      let schedule = await client.fetchSchedule(abort.signal, knownEpoch);
      let slots: ScheduleSlot[] | null =
        knownEpoch !== undefined && schedule.head.epoch === knownEpoch ? schedule.slots : null;
      if (slots === null) {
        try {
          const withSlots = await client.fetchSchedule(abort.signal, schedule.head.epoch);
          if (withSlots.head.epoch === schedule.head.epoch) {
            schedule = withSlots;
            slots = withSlots.slots;
          }
        } catch {
          // Head facts still render; the track stays bare until the next
          // poll lands a timeline for the current epoch.
        }
      }
      const startSlot = schedule.head.observed_slot - schedule.head.slot_index;
      let summary: EpochSummary | null = null;
      if (startSlot >= 0) {
        try {
          summary = await client.fetchEpochSummary(
            startSlot,
            startSlot + schedule.head.slots_in_epoch - 1,
            abort.signal,
          );
        } catch {
          // Schedule facts still render; so-far facts carry forward
          // (same-epoch only) via mergeLiveFacts.
          summary = null;
        }
      }
      if (abort.signal.aborted) {
        return;
      }
      this.#liveFacts = mergeLiveFacts(schedule, summary, this.#liveFacts, Date.now());
      if (slots !== null) {
        this.#liveSlots = {
          epoch: schedule.head.epoch,
          startSlot,
          slotsInEpoch: schedule.head.slots_in_epoch,
          slots,
        };
      } else if (this.#liveSlots !== null && this.#liveSlots.epoch !== schedule.head.epoch) {
        // Epoch rolled and the follow-up did not land: no stale ticks.
        this.#liveSlots = null;
      }
      this.#renderLive();
      // Panel sources refresh on the same shared poll — no second timer.
      void this.#refreshStreamData();
    } catch {
      if (abort.signal.aborted) {
        return;
      }
      this.#liveFailedOnce = true;
      // Keep the last facts; buildLiveSegments discloses their growing age.
      this.#renderLive();
    }
  }

  #renderLive(): void {
    const el = this.#liveEtaEl;
    const track = this.#liveTrackEl;
    if (el === null || track === null) {
      return;
    }
    if (this.#liveFacts === null) {
      if (this.#liveFailedOnce) {
        renderLiveUnavailable(el);
        renderLiveTrack(track, null, []);
        this.#liveRendered = true;
        this.#applyLiveVisibility();
      }
      return;
    }
    const now = Date.now();
    // Compact row: ETA tail only (stale age stays visible here); the full
    // sentence renders inside the datastream panel (refresh below).
    renderLiveStrip(el, buildEtaSegments(this.#liveFacts, now));
    const timeline =
      this.#liveSlots !== null && this.#liveSlots.epoch === this.#liveFacts.epoch
        ? this.#liveSlots
        : null;
    renderLiveTrack(
      track,
      this.#liveFacts.progressPct,
      timeline === null
        ? []
        : buildTrackTicks(timeline.slots, timeline.startSlot, timeline.slotsInEpoch),
      shouldBlinkCursor(this.#liveFacts, now),
    );
    this.#liveRendered = true;
    this.#applyLiveVisibility();
    if (this.#stream?.isOpen === true) {
      this.#stream.refresh(this.#streamSnapshot(this.#liveFacts.epoch));
    }
  }

  /** The live region shows once it has rendered content and always yields to the explorer (band visibility group). */
  #applyLiveVisibility(): void {
    this.#liveRegionEl?.classList.toggle("vf-hidden", this.#explorerOpen || !this.#liveRendered);
  }

  // --- blocks datastream panel ------------------------------------------------

  /**
   * Composes the panel's poll-time snapshot. Sources tagged with a
   * different epoch read as not-yet-fetched — an epoch roll never
   * re-labels the old epoch's timeline, quality row, or tips.
   */
  #streamSnapshot(epoch: number): StreamSnapshot {
    const slots =
      this.#liveSlots !== null && this.#liveSlots.epoch === epoch ? this.#liveSlots.slots : null;
    const quality =
      this.#streamSkips !== null && this.#streamSkips.epoch === epoch
        ? { fetched: true, row: this.#streamSkips.row }
        : { fetched: false, row: null };
    const tipsBySlot =
      this.#streamTips !== null && this.#streamTips.epoch === epoch
        ? this.#streamTips.tipsBySlot
        : null;
    // The panel's first line: the full live sentence (phase 3 — moved out
    // of the main view). Same epoch-tag rule as the other sources.
    const liveLine =
      this.#liveFacts !== null && this.#liveFacts.epoch === epoch
        ? buildLiveSegments(this.#liveFacts, Date.now())
        : null;
    return { epoch, slots, quality, tipsBySlot, liveLine };
  }

  /**
   * Live region activation. The panel is a FULL exclusivity participant
   * (phase 3, operator direction — overrules the phase-2 "coexists with
   * the income view" choice): opening it exits the income view to the
   * prior chart view (the income toggle's second-press path), closes the
   * explorer, and closes the income drill-down — one expanded surface at
   * a time, in every pairing. No-op before the first live facts — there
   * is no epoch to stream yet.
   */
  #toggleStream(): void {
    const stream = this.#stream;
    if (stream === null) {
      return;
    }
    if (stream.isOpen) {
      // Activation came from the trigger; focus is already on it.
      stream.hide();
      return;
    }
    const facts = this.#liveFacts;
    if (facts === null) {
      return;
    }
    const closedOthers = this.#closeOtherSurfaces("stream");
    if (closedOthers) {
      // The income view or explorer was open: re-render the restored
      // chart view before the panel opens below it.
      this.#renderFull();
    }
    stream.show(this.#streamSnapshot(facts.epoch));
    void this.#refreshStreamData();
  }

  /**
   * Fetches the panel's two joined sources — the /skips quality row and
   * the complete /blocks tips map for the epoch (complete-or-error
   * pagination via fetchEpochBlocks) — on panel open and on each shared
   * poll while open. Its own abort plane: aborting a chart load or live
   * poll must not cancel it, and vice versa. Either fetch failing keeps
   * the last facts (phase-1 rule); the epoch tags plus #streamSnapshot
   * keep a roll from re-labeling them.
   */
  async #refreshStreamData(): Promise<void> {
    const facts = this.#liveFacts;
    const base = this.getAttribute("api-base");
    if (
      facts === null ||
      this.#stream?.isOpen !== true ||
      base === null ||
      base.trim() === ""
    ) {
      return;
    }
    this.#streamAbort?.abort();
    const abort = new AbortController();
    this.#streamAbort = abort;
    const epoch = facts.epoch;
    const trimmed = base.trim();
    const [skipsResult, blocksResult] = await Promise.allSettled([
      createClient(trimmed).fetchSkips(epoch, abort.signal),
      fetchEpochBlocks(createFetchJson(trimmed), epoch, abort.signal),
    ]);
    if (abort.signal.aborted) {
      return;
    }
    if (skipsResult.status === "fulfilled") {
      this.#streamSkips = {
        epoch,
        row: skipsResult.value.find((r) => r.epoch === epoch) ?? null,
      };
    }
    if (blocksResult.status === "fulfilled") {
      this.#streamTips = {
        epoch,
        tipsBySlot: new Map(blocksResult.value.map((b) => [b.slot, b.tipsLamports])),
      };
    }
    if (this.#stream?.isOpen === true && this.#liveFacts !== null) {
      this.#stream.refresh(this.#streamSnapshot(this.#liveFacts.epoch));
    }
  }

  /**
   * Stream-row activation: opens the embedded income explorer at that
   * block through the existing toggle path (#toggleExplorer, which closes
   * this panel via #closeOtherSurfaces — one surface) and the explorer's
   * public navigateTo API.
   */
  #openExplorerAtBlock(epoch: number, slot: number): void {
    if (!this.#explorerOpen) {
      this.#toggleExplorer();
    }
    (this.#explorer as VfIncomeExplorer | null)?.navigateTo({ kind: "block", epoch, slot });
  }

  // --- shared: header title / inline metric ---------------------------------

  #headerTitleText(a: Assembled | null): string {
    return this.#basisOf(a) === "base" ? "Base delegator APY vs cluster" : "Net delegator APY vs cluster";
  }

  #headerMetricText(headline: HeadlineEpoch | null, a: Assembled | null): string {
    if (headline === null) {
      return this.#hasCoveredFor(a) ? "no validator APY at a covered epoch" : "no fully covered epoch";
    }
    const income = a?.byEpoch.get(headline.epoch)?.income;
    const ended = income?.end_time !== undefined ? formatUtcMonthDay(income.end_time) : "—";
    return `through epoch ${headline.epoch} · ended ${ended}`;
  }

  /**
   * Title + an explicit separator span + metric, appended to `header`. The
   * separator is a real, always-present element (not only the header's
   * flex `gap`) so the two text runs can never read as one concatenated
   * string regardless of render/copy path — the run-on bug ("...vs
   * clusterthrough epoch 1000") this fixes.
   */
  #appendHeaderTitleMeta(header: HTMLElement, titleText: string, metaText: string): void {
    const title = document.createElement("span");
    title.className = "vf-header-title";
    title.textContent = titleText;
    const sep = document.createElement("span");
    sep.className = "vf-header-sep";
    sep.setAttribute("aria-hidden", "true");
    sep.textContent = "·";
    const meta = document.createElement("span");
    meta.className = "vf-header-meta vf-mono";
    meta.textContent = metaText;
    header.append(title, sep, meta);
  }

  // --- full instrument -----------------------------------------------------

  #renderFull(): void {
    const windowed = this.#assembled;
    const headline = this.#headlineFor(windowed);

    this.#renderHeaderFull(windowed, headline);
    this.#renderAnswerBand(headline, windowed);
    this.#renderChart();
    this.#renderUnitSwitch(windowed);
    this.#renderChannelSwitch(windowed);
    this.#renderViewSwitch(windowed);
    this.#renderFooter(windowed);
    this.#applyExplorerSurfaces();

    this.#root?.setAttribute("aria-label", this.#ariaSummary(headline, windowed));
  }

  #renderHeaderFull(windowed: Assembled | null, headline: HeadlineEpoch | null): void {
    const header = this.#headerEl;
    if (header === null) {
      return;
    }
    header.replaceChildren();
    const metricText = this.#headerMetricText(headline, windowed);
    this.#appendHeaderTitleMeta(header, this.#headerTitleText(windowed), metricText);
    // Same fact, second surface: shown (by CSS) only while the collapse
    // ladder hides the header meta, so narrowing never deletes the
    // freshness claim.
    if (this.#freshnessEl !== null) {
      this.#freshnessEl.textContent = metricText;
      // Un-hide from a prior explorer session; #applyExplorerSurfaces
      // re-hides right after this render while the explorer is open.
      this.#freshnessEl.classList.remove("vf-hidden");
    }

    // No [all/90/30] window buttons: redundant with the dataZoom slider,
    // which already browses the full range natively (no separate hard
    // data-slice control needed). The former [stats] drawer toggle is
    // removed with its drawer (C57).
    const controls = document.createElement("div");
    controls.className = "vf-header-controls";
    // The view switch renders into a persistent element (skeleton-owned)
    // and is re-adopted here each render so header rebuilds never orphan
    // it. First in the cluster: view selection reads before surface
    // toggles.
    if (this.#viewSwitchEl !== null) {
      controls.append(this.#viewSwitchEl);
    }

    // [validator income] (C51): a VIEW toggle, like the explorer button —
    // it transforms the chart into the per-epoch net-income SOL line, and
    // a second press returns to the previous view. The label names WHOSE
    // income this is: an investor must never parse it as their own. The
    // former open-the-headline-panel behavior is removed; the panel now
    // opens only from a click in this view or the pin card's details.
    const incomeBtn = document.createElement("button");
    incomeBtn.type = "button";
    incomeBtn.className = "vf-btn vf-header-btn";
    incomeBtn.textContent = "validator income";
    incomeBtn.disabled = windowed === null;
    incomeBtn.setAttribute("aria-pressed", String(this.#view === "income"));
    incomeBtn.setAttribute("aria-description", VIEW_DESCRIPTIONS.income);
    incomeBtn.addEventListener("click", () => this.#toggleIncomeView());
    controls.append(incomeBtn);

    // "explorer": engineer-facing toggle swapping the chart surfaces for
    // the embedded income drill-down. Base mode only, like the band's view
    // tabs; net-only mode never renders it.
    if (this.#basisOf(windowed) === "base") {
      const explorerBtn = document.createElement("button");
      explorerBtn.type = "button";
      explorerBtn.className = "vf-btn vf-header-btn";
      explorerBtn.textContent = "explorer";
      explorerBtn.setAttribute("aria-pressed", String(this.#explorerOpen));
      explorerBtn.addEventListener("click", () => this.#toggleExplorer());
      controls.append(explorerBtn);
    }

    // Theme toggle (operator-requested): self-contained day/night control
    // so embeds need no page-level chrome. Sets the element's `theme`
    // attribute, which drives the one existing theme path (root class,
    // chart re-render, embedded-explorer mirror). Not a .vf-header-btn:
    // that class's rendered list is pinned as the labeled control
    // vocabulary; the box (.vf-btn) is identical.
    const themeBtn = document.createElement("button");
    themeBtn.type = "button";
    themeBtn.className = "vf-btn vf-theme-toggle";
    const targetTheme = this.#theme.name === "dark" ? "light" : "dark";
    themeBtn.setAttribute("aria-label", `switch to ${targetTheme} theme`);
    themeBtn.title = `switch to ${targetTheme} theme`;
    themeBtn.innerHTML = targetTheme === "dark" ? MOON_SVG : SUN_SVG;
    themeBtn.addEventListener("click", () => {
      this.setAttribute("theme", targetTheme);
      // The attribute change re-rendered the header; keep keyboard flow
      // on the (fresh) toggle.
      this.#headerEl
        ?.querySelector<HTMLButtonElement>(".vf-theme-toggle")
        ?.focus({ preventScroll: true });
    });
    controls.append(themeBtn);

    header.append(controls);
    this.#applyCollapseLadder(this.clientWidth);
  }

  /**
   * Toggles the embedded income explorer over the chart surfaces. The
   * explorer element is created on first open only (zero fetches until
   * then); #view/#zoom are untouched, so toggling back restores the same
   * plot and slider window.
   */
  #toggleExplorer(): void {
    this.#explorerOpen = !this.#explorerOpen;
    if (this.#explorerOpen) {
      // Mutual exclusivity (operator direction, C51 round): opening the
      // explorer exits the income view and hides the drill-down panel;
      // pins are cleared as before.
      this.#pins?.destroy();
      this.#closeOtherSurfaces("explorer");
      if (this.#explorer === null && this.#headerEl !== null) {
        const explorer = document.createElement("vf-income-explorer");
        // embedded: the host's .vf-root already carries the single frame.
        explorer.setAttribute("embedded", "");
        const base = this.getAttribute("api-base");
        if (base !== null) {
          explorer.setAttribute("api-base", base);
        }
        // Resolved theme, not the raw attribute: with the attribute
        // absent, this element resolves prefers-color-scheme and the
        // explorer must start on the same theme.
        explorer.setAttribute("theme", this.#theme.name);
        // No from-epoch/to-epoch pass-through: the explorer defaults its
        // own range.
        // Close request (the explorer's [x] or its top-level Escape):
        // route to this toggle — the one close path, shared with the
        // header button.
        explorer.addEventListener(EXPLORER_CLOSE_EVENT, () => {
          if (this.#explorerOpen) {
            this.#toggleExplorer();
          }
        });
        this.#explorer = explorer;
        this.#headerEl.after(explorer);
      }
    }
    this.#renderFull();
    // The header was rebuilt: keep keyboard flow on the toggle itself.
    const btn = [
      ...(this.#headerEl?.querySelectorAll<HTMLButtonElement>(".vf-header-btn") ?? []),
    ].find((b) => b.textContent === "explorer");
    btn?.focus({ preventScroll: true });
  }

  /**
   * [validator income] header toggle (C51): transforms the chart into the
   * per-epoch net-income SOL line; a second press returns to the previous
   * view. Entering and leaving both clear pins, the clicked-epoch mark,
   * and the drill-down panel — the panel opens only from a click IN the
   * view (never instantly on entry), and no click result outlives the
   * view that produced it.
   */
  #toggleIncomeView(): void {
    if (this.#view === "income") {
      this.#view = this.#viewBeforeIncome;
    } else {
      // Mutual exclusivity (operator direction, C51 round): entering the
      // income view closes the explorer first.
      this.#closeOtherSurfaces("income");
      this.#viewBeforeIncome = this.#view;
      this.#view = "income";
    }
    this.#pins?.destroy();
    this.#incomePinnedEpoch = null;
    this.#drilldown?.hide();
    this.#renderFull();
    // The header was rebuilt: keep keyboard flow on the toggle itself.
    const btn = [
      ...(this.#headerEl?.querySelectorAll<HTMLButtonElement>(".vf-header-btn") ?? []),
    ].find((b) => b.textContent === "validator income");
    btn?.focus({ preventScroll: true });
  }

  /**
   * The unit the income view actually plots: "usd" only when chosen AND
   * the fetched /income rows carry the USD fields. A reload already resets
   * the choice, but degrading here keeps a stale "usd" from ever plotting
   * an all-null series against data without the fields.
   */
  #effectiveIncomeUnit(a: Assembled | null): IncomeUnit {
    return this.#incomeUnit === "usd" && a !== null && usdFieldsPresent(a) ? "usd" : "sol";
  }

  /**
   * SOL|USD unit switch, inside the income view only and ONLY when the
   * fetched /income rows carry the USD valuation fields — an older daemon
   * renders no control at all (absence is a real state, not a disabled
   * button). aria-pressed marks the active segment; the label is the
   * accessible name, the description states what activation shows.
   */
  #renderUnitSwitch(windowed: Assembled | null): void {
    const el = this.#unitSwitchEl;
    if (el === null) {
      return;
    }
    const inIncome = windowed !== null && this.#activeView(windowed) === "income";
    // The [x] shows whenever the income view is open (operator close
    // grammar: every closable surface carries [x]); the unit segments
    // additionally need the daemon to serve the USD fields.
    this.#incomeControlsEl?.classList.toggle("vf-hidden", !inIncome);
    const show = inIncome && windowed !== null && usdFieldsPresent(windowed);
    el.classList.toggle("vf-hidden", !show);
    el.replaceChildren();
    if (!show) {
      return;
    }
    const active = this.#effectiveIncomeUnit(windowed);
    const segments: { unit: IncomeUnit; label: string; description: string }[] = [
      { unit: "sol", label: "SOL", description: "shows the per-epoch net income line in SOL" },
      {
        unit: "usd",
        label: "USD",
        description:
          "shows per-epoch block fees valued in USD at each block's earn-time price (not net)",
      },
    ];
    for (const seg of segments) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "vf-btn vf-unit-btn";
      btn.textContent = seg.label;
      btn.setAttribute("aria-pressed", String(active === seg.unit));
      btn.setAttribute("aria-description", seg.description);
      btn.addEventListener("click", () => this.#setIncomeUnit(seg.unit));
      el.append(btn);
    }
  }

  /** Switches the income view's plotted unit; the pinned epoch/panel survive (same epoch domain, panel stays lamports/SOL). */
  #setIncomeUnit(unit: IncomeUnit): void {
    if (this.#incomeUnit === unit) {
      return;
    }
    this.#incomeUnit = unit;
    this.#renderChart();
    this.#renderFooter(this.#assembled);
    this.#renderUnitSwitch(this.#assembled);
    // Channel toggles are SOL-mode instruments: USD hides the group
    // entirely (its one line has no channel decomposition).
    this.#renderChannelSwitch(this.#assembled);
    // The switch was rebuilt: keep keyboard flow on the pressed segment.
    this.#unitSwitchEl
      ?.querySelector<HTMLButtonElement>('[aria-pressed="true"]')
      ?.focus({ preventScroll: true });
  }

  /**
   * [net][fees][tips][bam] additive channel toggles, income view SOL mode
   * only (USD renders no group at all — one line, no channels).
   *
   * Multi-select: each segment independently toggles its line(s);
   * aria-pressed is the enabled state. At least one line stays on — the
   * sole enabled segment carries aria-disabled="true" and its press is
   * ignored (documented choice: a no-op with disabled semantics, so the
   * control never offers an empty plot).
   */
  #renderChannelSwitch(windowed: Assembled | null): void {
    const el = this.#channelSwitchEl;
    if (el === null) {
      return;
    }
    const show =
      windowed !== null &&
      this.#activeView(windowed) === "income" &&
      this.#effectiveIncomeUnit(windowed) === "sol";
    el.classList.toggle("vf-hidden", !show);
    el.replaceChildren();
    if (!show) {
      return;
    }
    const descriptions: Record<IncomeChannel, string> = {
      net: "toggles the per-epoch net income line (ledger net_total, SOL)",
      fees: "toggles the per-epoch block fees line (SOL)",
      tips: "toggles the tips witness pair: Kobe-settled solid and block-observed dashed lines (SOL)",
      bam: "toggles the per-epoch BAM boost line (SOL)",
    };
    for (const channel of INCOME_CHANNEL_ORDER) {
      const enabled = this.#incomeChannels.has(channel);
      const lastActive = enabled && this.#incomeChannels.size === 1;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "vf-btn vf-channel-btn";
      btn.textContent = channel;
      btn.setAttribute("aria-pressed", String(enabled));
      btn.setAttribute("aria-description", descriptions[channel]);
      if (lastActive) {
        btn.setAttribute("aria-disabled", "true");
      }
      btn.addEventListener("click", () => this.#toggleIncomeChannel(channel));
      el.append(btn);
    }
  }

  /** Toggles one channel's line(s); ignores the press that would blank the plot (last-line guard). */
  #toggleIncomeChannel(channel: IncomeChannel): void {
    if (this.#incomeChannels.has(channel)) {
      if (this.#incomeChannels.size === 1) {
        return;
      }
      this.#incomeChannels.delete(channel);
    } else {
      this.#incomeChannels.add(channel);
    }
    this.#renderChart();
    // The tips footnote enters/leaves with its line pair.
    this.#renderFooter(this.#assembled);
    this.#renderChannelSwitch(this.#assembled);
    // The group was rebuilt: keep keyboard flow on the pressed segment.
    const idx = INCOME_CHANNEL_ORDER.indexOf(channel);
    this.#channelSwitchEl
      ?.querySelectorAll<HTMLButtonElement>(".vf-channel-btn")
      [idx]?.focus({ preventScroll: true });
  }

  /**
   * apy|position|mev segmented control at the plot's top-right.
   *
   * The visible counterpart of the answer-band view tabs (C51
   * discoverability: the band cells silently double as tabs — nothing
   * signals they are clickable). Segments activate through the SAME
   * #setView path the band tabs use, and both selectors read #view on
   * render, so the two indicators can never disagree. Gating mirrors the
   * band tabs (base mode only — net-only mode has no alternate views);
   * the control also leaves with the income view, whose SOL|USD unit
   * switch owns the same corner, and under the explorer.
   */
  #renderViewSwitch(windowed: Assembled | null): void {
    const el = this.#viewSwitchEl;
    if (el === null) {
      return;
    }
    const show =
      windowed !== null &&
      this.#basisOf(windowed) === "base" &&
      this.#activeView(windowed) !== "income" &&
      !this.#explorerOpen;
    el.classList.toggle("vf-hidden", !show);
    el.replaceChildren();
    if (!show) {
      return;
    }
    const segments: { view: ChartView; label: string }[] = [
      { view: "base", label: "apy" },
      { view: "position", label: "position" },
      { view: "mev", label: "mev" },
    ];
    for (const seg of segments) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "vf-btn vf-view-btn";
      btn.textContent = seg.label;
      btn.setAttribute("aria-pressed", String(this.#view === seg.view));
      btn.setAttribute("aria-description", VIEW_DESCRIPTIONS[seg.view]);
      btn.addEventListener("click", () => {
        this.#setView(seg.view);
        // #setView re-rendered every selector (and moved focus to the
        // active band tab); keyboard flow stays on the control the user
        // actually pressed. A same-view press re-renders nothing and the
        // pressed segment simply keeps focus.
        this.#viewSwitchEl
          ?.querySelector<HTMLButtonElement>('[aria-pressed="true"]')
          ?.focus({ preventScroll: true });
      });
      el.append(btn);
    }
  }

  /**
   * Explorer mode: the header stays; the answer band, chart, captions, pin
   * lane, and footer hide beneath the embedded income explorer.
   * Applied after every full render so a theme/data re-render cannot
   * resurface a hidden element while the explorer is open.
   */
  #applyExplorerSurfaces(): void {
    const open = this.#explorerOpen && this.#explorer !== null;
    this.#explorer?.classList.toggle("vf-hidden", !open);
    this.#bandEl?.classList.toggle("vf-hidden", open);
    // The live region (strip + track) belongs to the band's visibility
    // group: hidden exactly when the band is hidden, in every chart view
    // otherwise (it additionally stays hidden until its first render).
    this.#applyLiveVisibility();
    for (const surface of [this.#chartWrapEl, this.#pinLaneEl]) {
      surface?.classList.toggle("vf-hidden", open);
    }
    if (open) {
      this.#viewFootnoteEl?.classList.add("vf-hidden");
      this.#gapCaptionEl?.classList.add("vf-hidden");
      this.#freshnessEl?.classList.add("vf-hidden");
      this.#drilldownHost?.classList.add("vf-hidden");
      this.#streamHostEl?.classList.add("vf-hidden");
    } else {
      // Un-hiding after display:none: ECharts must re-measure.
      this.#chart?.resize();
    }
    if (!open) {
      return;
    }
    this.#footerEl?.classList.add("vf-hidden");
    const title = this.#headerEl?.querySelector(".vf-header-title");
    if (title !== null && title !== undefined) {
      title.textContent = "Operator income drill-down";
    }
    this.#headerEl?.querySelector(".vf-header-sep")?.classList.add("vf-hidden");
    this.#headerEl?.querySelector(".vf-header-meta")?.classList.add("vf-hidden");
  }

  /**
   * Mutual exclusivity of the expanded surfaces (operator direction, C51
   * round; extended phase 3): opening the income view, the explorer, or
   * the blocks datastream panel closes the others first — they never
   * stack, so the widget cannot grow endlessly. The income drill-down
   * panel closes with them regardless of which control opened it (it is
   * a click-expanded detail surface). Returns true when anything was
   * closed (callers then need a full re-render).
   */
  #closeOtherSurfaces(keep: "income" | "explorer" | "stream"): boolean {
    let closed = false;
    // The blocks datastream panel is a full participant (phase 3,
    // operator direction — overrules the phase-2 detail-surface-only
    // form): it closes whenever another surface opens, and its own
    // opener closes the income view, the explorer, and the drill-down
    // through this same switch (keep="stream").
    if (keep !== "stream" && this.#stream?.isOpen === true) {
      this.#stream.hide();
      closed = true;
    }
    if (keep !== "income") {
      if (this.#view === "income") {
        // Exit to the prior chart view; the clicked-epoch mark dies with
        // the view.
        this.#view = this.#viewBeforeIncome;
        this.#incomePinnedEpoch = null;
        closed = true;
      }
      if (this.#drilldown !== null && this.#drilldown.openEpoch !== null) {
        this.#drilldown.hide();
        closed = true;
      }
    }
    if (keep !== "explorer" && this.#explorerOpen) {
      this.#explorerOpen = false;
      closed = true;
    }
    return closed;
  }

  // Formerly also rendered a footer provenance line ("Epochs A-B. Gaps
  // are disclosed..."); removed by operator direction 2026-07-14 — the
  // gap policy is enforced by the rendering itself (gaps are visible),
  // the range is on the axis, and the header meta carries freshness.
  #renderFooter(windowed: Assembled | null): void {
    if (windowed === null) {
      return;
    }
    const view = this.#activeView(windowed);

    // Base mode: the plot hatches the BASE distribution gate (rare/never
    // once the API deploys, since base needs no stake capture), matching
    // what actually renders in the chart. The hatch is a base-view mark;
    // the position/mev plots carry no gated markArea, so the caption
    // hides with it there.
    const basePrimary = this.#basisOf(windowed) === "base";
    const gated = basePrimary ? windowed.distributionInflGated : windowed.distributionGated;
    const pending = basePrimary ? windowed.distributionInflPending : windowed.distributionPending;
    const hasGated = view === "base" && gated.some((g) => g);
    // Pending rides the same caption slot but is its own sentence: the two
    // absences have different causes and must never be captioned as one.
    const hasPending = view === "base" && pending.some((p) => p);
    if (this.#gapCaptionEl !== null) {
      const parts: string[] = [];
      if (hasGated) {
        parts.push(GATED_CAPTION);
      }
      if (hasPending) {
        parts.push(PENDING_CAPTION);
      }
      this.#gapCaptionEl.textContent = parts.join(" · ");
      this.#gapCaptionEl.classList.toggle("vf-hidden", parts.length === 0);
    }

    // View-specific derivation/absence footnote: the MEV view discloses
    // its derivation and the missing cluster context; the position view
    // states the percentile definition and the display scale (floor from
    // the same percentile array the view plots); the income view's USD
    // mode states what the USD line is (block fees at receipt) and is not
    // (net). The base view and the SOL income mode need none (their
    // derivations live in the tooltip).
    if (this.#viewFootnoteEl !== null) {
      // Income view: USD and tips footnotes cannot both apply — the
      // channel toggles (and so the tips pair) exist only in SOL mode.
      const incomeFootnote =
        this.#effectiveIncomeUnit(windowed) === "usd"
          ? INCOME_USD_FOOTNOTE
          : this.#incomeChannels.has("tips")
            ? INCOME_TIPS_FOOTNOTE
            : null;
      const text =
        view === "mev"
          ? MEV_VIEW_FOOTNOTE
          : view === "position"
            ? positionViewFootnote(percentileFloor(windowed.ourBasePercentile))
            : view === "income"
              ? incomeFootnote
              : null;
      this.#viewFootnoteEl.textContent = text ?? "";
      this.#viewFootnoteEl.classList.toggle("vf-hidden", text === null);
    }
  }

  // --- chart ---------------------------------------------------------------

  /**
   * The plot currently rendered. The band-tab alternates (position, mev)
   * exist only in base mode (their columns are base-mode data); net-only
   * mode renders the unchanged base ("net primary") chart. The income view
   * is basis-independent — /apy net_total_lamports serves in both modes.
   */
  #activeView(a: Assembled | null): ChartView {
    if (this.#view === "income") {
      return "income";
    }
    return this.#basisOf(a) === "base" ? this.#view : "base";
  }

  /** dataIndex of an axis-trigger tooltip callback's first entry, or -1. */
  #tooltipDataIndex(params: unknown): number {
    const first = Array.isArray(params) ? params[0] : params;
    return typeof (first as { dataIndex?: number } | undefined)?.dataIndex === "number"
      ? (first as { dataIndex: number }).dataIndex
      : -1;
  }

  #renderChart(): void {
    const windowed = this.#assembled;
    const host = this.#chartHost;
    const wrap = this.#chartWrapEl;
    if (windowed === null || host === null || wrap === null) {
      return;
    }
    const t = this.#theme;

    if (this.#chart === null) {
      this.#chart = echarts.init(host, undefined, { renderer: "svg" });
      this.#chart.on("legendselectchanged", (params: unknown) => {
        const selected = (params as { selected?: Record<string, boolean> } | undefined)?.selected;
        if (selected) {
          this.#legendSelected = { ...selected };
        }
      });
      // Capture the live slider window so re-renders (pin ticks) and view
      // switches restore it instead of resetting to the full range.
      this.#chart.on("datazoom", () => {
        const dz = (this.#chart?.getOption()["dataZoom"] as
          | { start?: number; end?: number }[]
          | undefined)?.[0];
        if (dz !== undefined && typeof dz.start === "number" && typeof dz.end === "number") {
          this.#zoom = { start: dz.start, end: dz.end };
        }
      });
    }
    this.#bindClick();

    const dateForEpoch = (epoch: number): number | null =>
      windowed.byEpoch.get(epoch)?.income?.start_time ?? null;
    const view = this.#activeView(windowed);

    // The full chart opens on exactly the epochs the answer band's positional
    // claim is measured over ("median of last 45 epochs"), so the claim's
    // evidence is on screen instead of four slider-drags away. One knob:
    // position-window sets both, so they cannot drift apart. The three full
    // chart views share one coordinate system, so they share the default.
    const fullChartDefaultZoom = defaultZoomWindow(windowed.epochs.length, this.#positionWindow);
    const layout = this.#chartLayout();

    let option;
    if (view === "position") {
      option = buildPositionOption({
        a: windowed,
        theme: t,
        dateForEpoch,
        tooltipAppendTo: wrap,
        zoom: this.#zoom ?? fullChartDefaultZoom,
        layout,
        tooltipFormatter: (params: unknown): string => this.#positionTooltipHtml(windowed, params, t),
      });
    } else if (view === "mev") {
      option = buildMevOption({
        a: windowed,
        theme: t,
        dateForEpoch,
        tooltipAppendTo: wrap,
        zoom: this.#zoom ?? fullChartDefaultZoom,
        layout,
        tooltipFormatter: (params: unknown): string => this.#mevTooltipHtml(windowed, params, t),
      });
    } else if (view === "income") {
      const unit = this.#effectiveIncomeUnit(windowed);
      option = buildIncomeOption({
        a: windowed,
        theme: t,
        dateForEpoch,
        tooltipAppendTo: wrap,
        // undefined, not a default window: the income view computes its own
        // served-data span (a young ledger left 99% of the full range empty).
        zoom: this.#zoom ?? undefined,
        layout,
        pinnedEpoch: this.#incomePinnedEpoch,
        unit,
        channels: this.#incomeChannels,
        tooltipFormatter: (params: unknown): string =>
          this.#incomeTooltipHtml(windowed, params, t, unit),
      });
    } else {
      option = buildOption({
        a: windowed,
        theme: t,
        pinnedEpoch: this.#pins?.pinnedEpoch ?? null,
        dateForEpoch,
        tooltipAppendTo: wrap,
        zoom: this.#zoom ?? fullChartDefaultZoom,
        layout,
        tooltipFormatter: (params: unknown): string => {
          const idx = this.#tooltipDataIndex(params);
          const epoch = windowed.epochs[idx];
          if (epoch === undefined) {
            return "";
          }
          const joined = windowed.byEpoch.get(epoch);
          return viewToHtml(
            epochHeader(epoch, joined),
            buildEpochView(joined, t, this.#basisOf(windowed), this.#netRevealed()),
            t,
          );
        },
        legendSelected: this.#legendSelected,
      });
    }
    // Resolve the keyboard tooltip target by NAME: the base view's hero
    // line (unique name per basis), or the alternate views' single series.
    const seriesList = Array.isArray(option.series) ? (option.series as { name?: string }[]) : [];
    const heroName =
      view === "base" ? (this.#basisOf(windowed) === "base" ? SERIES_OUR_BASE : SERIES_OUR) : null;
    const heroIndex = heroName === null ? 0 : seriesList.findIndex((s) => s.name === heroName);
    this.#tipSeriesIndex = Math.max(heroIndex, 0);
    // The N legend-toggle key exists only while the rendered option
    // carries the net series; the advertised key list follows it.
    this.#netToggleAvailable =
      view === "base" && seriesList.some((s) => s.name === SERIES_OUR_NET_DASHED);
    host.setAttribute(
      "aria-label",
      this.#netToggleAvailable ? CHART_KEYS_LABEL_WITH_NET : CHART_KEYS_LABEL,
    );
    this.#chart.setOption(option, { notMerge: true });
  }

  /** Position-view tooltip: the same row builder the sibling consistency card uses (consistency.ts). */
  #positionTooltipHtml(windowed: Assembled, params: unknown, t: ThemeTokens): string {
    const idx = this.#tooltipDataIndex(params);
    const epoch = windowed.epochs[idx];
    if (epoch === undefined) {
      return "";
    }
    const joined = windowed.byEpoch.get(epoch);
    const rows = percentileTooltipLines(
      {
        percentile: windowed.ourBasePercentile[idx] ?? null,
        rank: windowed.ourBaseRank[idx] ?? null,
        // Raw measured count from the cluster row (the rank's true
        // denominator), not the coverage-gated series: the gate withholds
        // network statistics, not the measured n itself.
        distributionValidators: joined?.cluster?.distribution_infl_validators ?? null,
        ourBaseApyPct: joined?.apy?.delegator_apy_inflation_pct ?? null,
        clusterP50BaseApyPct: joined?.cluster?.delegator_apy_infl_p50_pct ?? null,
      },
      t,
    );
    return viewToHtml(epochHeader(epoch, joined), { groups: [{ label: null, marker: null, rows }], trailing: [] }, t);
  }

  /**
   * Income-view tooltip. SOL: the explorer's level-1 epoch header
   * ("Epoch N · net X SOL") and income-expenses derivation, from the same
   * joined /income + /apy rows (income-explorer-chart.ts
   * epochChannelPoint). USD: the block-fees-at-receipt header, coverage,
   * and the not-yet-valued disclosure (income-view.ts builders).
   */
  #incomeTooltipHtml(windowed: Assembled, params: unknown, t: ThemeTokens, unit: IncomeUnit): string {
    const idx = this.#tooltipDataIndex(params);
    const epoch = windowed.epochs[idx];
    if (epoch === undefined) {
      return "";
    }
    const joined = windowed.byEpoch.get(epoch);
    if (unit === "usd") {
      return viewToHtml(
        incomeUsdTooltipTitle(epoch, joined?.income),
        incomeUsdTooltipView(joined?.income, t),
        t,
      );
    }
    const point = epochChannelPoint(epoch, joined?.income, joined?.apy);
    return viewToHtml(epochTooltipTitle(point), epochTooltipView(point, t), t);
  }

  /** MEV-view tooltip: component + its two inputs, written absence when either is missing. */
  #mevTooltipHtml(windowed: Assembled, params: unknown, t: ThemeTokens): string {
    const idx = this.#tooltipDataIndex(params);
    const epoch = windowed.epochs[idx];
    if (epoch === undefined) {
      return "";
    }
    const joined = windowed.byEpoch.get(epoch);
    const rows = mevTooltipLines(
      {
        netPct: joined?.apy?.delegator_apy_pct ?? null,
        basePct: joined?.apy?.delegator_apy_inflation_pct ?? null,
        tipsGrossLamports: joined?.income?.tips_gross_lamports ?? null,
        mevClaimedLamports: joined?.income?.mev_claimed_lamports ?? null,
      },
      t,
    );
    return viewToHtml(epochHeader(epoch, joined), { groups: [{ label: null, marker: null, rows }], trailing: [] }, t);
  }

  #bindClick(): void {
    const chart = this.#chart;
    if (chart === null || this.#clickBound) {
      return;
    }
    this.#clickBound = true;
    chart.getZr().on("click", (ev: { offsetX: number; offsetY: number }) => {
      const c = this.#chart;
      const windowed = this.#assembled;
      if (c === null || windowed === null) {
        return;
      }
      // Click grammar per view: base pins the epoch card; the income view
      // pins the epoch (amber mark) and expands the drill-down panel; the
      // position/mev views neither pin nor dismiss (nothing pinned there).
      const view = this.#activeView(windowed);
      if (view !== "base" && view !== "income") {
        return;
      }
      const point: [number, number] = [ev.offsetX, ev.offsetY];
      if (!c.containPixel("grid", point)) {
        return;
      }
      const xValue = c.convertFromPixel({ xAxisIndex: 0 }, ev.offsetX);
      const idx = Math.round(typeof xValue === "number" ? xValue : Number.NaN);
      const epoch = windowed.epochs[idx];
      if (epoch === undefined) {
        if (view === "income") {
          // Hiding the panel clears the mark (visibility callback).
          this.#drilldown?.hide();
        } else {
          this.#pins?.dismiss();
        }
        return;
      }
      if (view === "income") {
        this.#openIncomeEpoch(windowed, epoch);
      } else {
        this.#openPinForEpoch(windowed, epoch);
      }
      // The pinned card supersedes the transient tooltip; on touch the
      // tooltip otherwise stays (no mouseleave ever fires) and covers
      // the plot it annotated.
      c.dispatchAction({ type: "hideTip" });
    });
  }

  #onChartKeydown(ev: KeyboardEvent): void {
    const windowed = this.#assembled;
    if (windowed === null) {
      return;
    }
    const last = windowed.epochs.length - 1;
    if (ev.key === "ArrowLeft") {
      ev.preventDefault();
      this.#keyboardCursor = Math.max(0, this.#keyboardCursor - 1);
      this.#showTransientTip(this.#keyboardCursor);
      this.#announceCursorEpoch(windowed);
    } else if (ev.key === "ArrowRight") {
      ev.preventDefault();
      this.#keyboardCursor = Math.min(last, this.#keyboardCursor + 1);
      this.#showTransientTip(this.#keyboardCursor);
      this.#announceCursorEpoch(windowed);
    } else if (ev.key === "n" || ev.key === "N") {
      // Legend toggle for the net incl MEV series — its only other reveal
      // is a pointer click on the legend's SVG shapes (keyboard-
      // unreachable). Available only when the rendered option carries it.
      if (!this.#netToggleAvailable) {
        return;
      }
      ev.preventDefault();
      const wasSelected = this.#legendSelected[SERIES_OUR_NET_DASHED] ?? false;
      this.#chart?.dispatchAction({ type: "legendToggleSelect", name: SERIES_OUR_NET_DASHED });
      this.#announce(`${SERIES_OUR_NET_DASHED} series ${wasSelected ? "hidden" : "shown"}`);
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      // Same per-view grammar as the pointer path (see #bindClick).
      const view = this.#activeView(windowed);
      if (view !== "base" && view !== "income") {
        return;
      }
      const epoch = windowed.epochs[this.#keyboardCursor];
      if (epoch === undefined) {
        return;
      }
      if (view === "income") {
        this.#openIncomeEpoch(windowed, epoch);
      } else {
        this.#openPinForEpoch(windowed, epoch);
      }
    } else if (ev.key === "Escape") {
      if (this.#activeView(windowed) === "income") {
        // Dismiss the income view's click result: panel and mark together.
        this.#drilldown?.hide();
      } else {
        this.#pins?.dismiss();
      }
    }
  }

  /** Accessible output for an arrow-key move: the epoch header line into the polite live region (the transient tooltip is visual-only). */
  #announceCursorEpoch(windowed: Assembled): void {
    const epoch = windowed.epochs[this.#keyboardCursor];
    if (epoch === undefined) {
      return;
    }
    this.#announce(epochHeaderShort(epoch, windowed.byEpoch.get(epoch)));
  }

  #showTransientTip(dataIndex: number): void {
    this.#chart?.dispatchAction({
      type: "showTip",
      seriesIndex: this.#tipSeriesIndex,
      dataIndex,
    });
  }

  /** See #keyboardCursor: headline epoch's index, falling back to the newest epoch. */
  #resetKeyboardCursor(): void {
    const a = this.#assembled;
    if (a === null || a.epochs.length === 0) {
      this.#keyboardCursor = 0;
      return;
    }
    const headline = this.#headlineFor(a);
    const idx = headline === null ? -1 : a.epochs.indexOf(headline.epoch);
    this.#keyboardCursor = idx >= 0 ? idx : a.epochs.length - 1;
  }

  /**
   * Income-view click/Enter on an epoch: pins it (amber mark line — the
   * pin CARD does not fit here, it would duplicate the decomposition the
   * panel below already shows) and expands the income drill-down panel
   * for it. The panel's Esc/[x] behavior is unchanged; hiding it clears
   * the mark via the visibility callback.
   */
  #openIncomeEpoch(windowed: Assembled, epoch: number): void {
    const joined = windowed.byEpoch.get(epoch);
    this.#incomePinnedEpoch = epoch;
    this.#showDrilldown(epoch, joined?.income, joined?.apy);
    this.#renderChart();
  }

  /** One detail surface below the plot: showing the income drill-down closes the blocks datastream panel first. */
  #showDrilldown(
    epoch: number,
    income: Parameters<DrilldownPanel["show"]>[1],
    apy: Parameters<DrilldownPanel["show"]>[2],
  ): void {
    this.#stream?.hide();
    this.#drilldown?.show(epoch, income, apy);
  }

  #openPinForEpoch(windowed: Assembled, epoch: number): void {
    const joined = windowed.byEpoch.get(epoch);
    if (this.#pins?.pinnedEpoch === epoch) {
      return;
    }
    const view = buildEpochView(joined, this.#theme, this.#basisOf(windowed), this.#netRevealed());
    const rows: PinData["rows"] = [];
    for (const g of view.groups) {
      if (g.label !== null) {
        rows.push({ label: g.label, value: "", heading: true });
      }
      for (const l of g.rows) {
        rows.push({ label: l.label, value: l.value });
      }
    }
    for (const l of view.trailing) {
      rows.push({ label: l.label, value: l.value });
    }
    const data: PinData = {
      epoch,
      header: epochHeaderShort(epoch, joined),
      rows,
      copyPayload: buildEpochCopyPayload(epoch, joined, view),
      onDetails: () => this.#showDrilldown(epoch, joined?.income, joined?.apy),
    };
    this.#pins?.open(data);
  }

  // --- shared: answer band -------------------------------------------------

  #renderAnswerBand(headline: HeadlineEpoch | null, a: Assembled | null): void {
    const band = this.#bandEl;
    if (band === null) {
      return;
    }
    band.replaceChildren();
    const basis = this.#basisOf(a);
    const inBase = basis === "base";

    if (headline === null) {
      const placeholder = document.createElement("div");
      placeholder.className = "vf-band-placeholder";
      // Distinguish "the latest epoch is gated" (name the real reason) from
      // "there is no distribution data at all" (generic no-positional-claim
      // text).
      const latestGated = this.#latestGatedCoverage(a, basis);
      placeholder.textContent =
        latestGated === null
          ? this.#hasCoveredFor(a)
            ? "no validator APY at a covered epoch"
            : "no fully covered epoch in selected range"
          : coverageGateReason(latestGated.measured, latestGated.total, "gated");
      band.append(placeholder);
      return;
    }

    // One label/value/detail grammar for every cell (11/500 label, 15/600
    // mono value — 26/600 only for cell 1's headline figure — 12/400
    // detail). Financial-logic redesign: in a tight distribution, ordinal
    // rank without economic magnitude misleads (a 0.0001pp gap to the
    // cluster max reads as "20 validators are better, leave" when the true
    // difference is a fraction of a cent per 1,000 SOL/yr). Cells 3/4 state
    // the comparison AND its economic size; vote-credit rank and the
    // base-yield percentile rank are demoted to 11px subdetail lines inside
    // them — never a "top X%" headline value anywhere in the band.
    const basisWord = inBase ? "base" : "net";
    const basisSuffix = inBase ? " (base)" : "";

    // Headline figure and the cluster deltas (cells 1/3/4) read a trailing
    // REALIZED APY, not a single epoch. A lone epoch's annualized APY carries a
    // duration artifact (a long/short epoch mis-states its rate) and reads as a
    // stable yearly figure it is not; trailingRealizedApy annualizes the yield
    // actually realized over the last HEADLINE_TRAILING_DAYS of real time --
    // the SEC 30-day-yield construction. Inputs are the SAME gated series the
    // chart plots (ours/median/max) plus each epoch's duration (byEpoch is
    // raw/ungated, so duration is read there). When no epoch is served on any
    // of the three series inside the window, fall back to the single covered
    // headline epoch so the band never blanks.
    const durations = a?.epochs.map((e) => a.byEpoch.get(e)?.apy?.duration_secs ?? null) ?? [];
    const ourSeries = inBase ? (a?.ourBase ?? []) : (a?.our ?? []);
    const medianSeries = inBase ? (a?.medianBase ?? []) : (a?.median ?? []);
    const maxSeries = inBase ? (a?.maxBase ?? []) : (a?.max ?? []);
    const p90Series = inBase ? (a?.p90Base ?? []) : (a?.p90 ?? []);
    const gatedSeries = inBase ? (a?.distributionInflGated ?? []) : (a?.distributionGated ?? []);
    // The three APY legs (ours / median / best) are computed over ONE shared
    // epoch set: the FULLY-COVERED epochs — our + median + p90 + max all
    // present and the epoch not coverage-gated — the same per-epoch predicate
    // selectHeadlineFrom uses. Our own value is never coverage-gated but the
    // cluster median/max are (max tighter than median), so without this mask
    // each leg averages a different epoch set and the vs-median / vs-best
    // deltas silently mix time bases, reintroducing the single-epoch duration
    // artifact this band exists to remove (audit HIGH). In normal full
    // coverage the mask is a no-op.
    const covered = (a?.epochs ?? []).map(
      (_e, i) =>
        gatedSeries[i] !== true &&
        present(ourSeries[i]) &&
        present(medianSeries[i]) &&
        present(p90Series[i]) &&
        present(maxSeries[i]),
    );
    const maskToCovered = (arr: readonly (number | null)[]): (number | null)[] =>
      arr.map((v, i) => (covered[i] ? v : null));
    const tOur = trailingRealizedApy(maskToCovered(ourSeries), durations, BAND_TRAILING_DAYS);
    const tP50 = trailingRealizedApy(maskToCovered(medianSeries), durations, BAND_TRAILING_DAYS);
    const tMax = trailingRealizedApy(maskToCovered(maxSeries), durations, BAND_TRAILING_DAYS);
    const useTrailing = tOur.apy !== null && tP50.apy !== null && tMax.apy !== null;
    const ourPct = useTrailing && tOur.apy !== null ? tOur.apy : headline.ourPct;
    const p50Pct = useTrailing && tP50.apy !== null ? tP50.apy : headline.p50;
    const maxPct = useTrailing && tMax.apy !== null ? tMax.apy : headline.max;
    // Window label. Solana epochs are ~2-day discrete chunks, so the trailing
    // window can never land on exactly 30 days -- it takes 15 whole epochs to
    // cover 30 days, which overshoots to ~31. When the window is full (covered
    // span >= the target) label the nominal target -- the SEC-30-day-yield
    // name institutions recognize, and a slight UNDERSTATEMENT of the real
    // lookback (the safe direction). Only when history is genuinely thinner
    // than the target does the label state the smaller real span (a gated
    // stretch likewise shrinks it -- audit MEDIUM). Singular grammar for 1 day.
    const trailingDays =
      tOur.spanDays >= BAND_TRAILING_DAYS ? BAND_TRAILING_DAYS : Math.max(1, Math.round(tOur.spanDays));
    const trailingSpanLabel = `trailing ${trailingDays} day${trailingDays === 1 ? "" : "s"}`;

    // Cell 1: headline figure.
    const cell1 = document.createElement("div");
    cell1.className = "vf-band-cell";
    const label1 = document.createElement("div");
    label1.className = "vf-band-label";
    label1.textContent = `${basisWord} APY`;
    const figure = document.createElement("div");
    figure.className = "vf-band-value-lg vf-mono";
    figure.textContent = `${ourPct.toFixed(2)}%`;
    const detail1 = document.createElement("div");
    detail1.className = "vf-band-detail";
    detail1.textContent = useTrailing
      ? `${trailingSpanLabel} · through epoch ${headline.epoch}`
      : headline.usedFallback
        ? `epoch ${headline.epoch} (most recent covered epoch)`
        : `epoch ${headline.epoch}`;
    cell1.append(label1, figure, detail1);

    // Vote-credit rank lives here, not under "vs cluster median". It is a
    // CONSENSUS ordinal from /rank, measured over a different population
    // (validators, not the yield distribution): filed beneath a yield delta
    // it reads as one compound claim -- "+28.2 bps above the median, rank 29
    // of 707" -- and the reader concludes 29th is our base-yield standing.
    // It is not; that is cell 4's "base-yield rank 19 of 706". A false
    // attribution produced purely by placement. Cell 1 makes no cluster
    // comparison the rank could falsify. The rank is a single-epoch ordinal,
    // but the figure above it is now a trailing statistic, so the rank line
    // carries its own "· epoch N" tag: its time basis is stated where it is
    // read, not inferred from the figure. (Not cell 2: its value is a
    // trailing-days MEDIAN, so a single-epoch ordinal beneath it would read as
    // a windowed statistic that was never measured.)
    const rank = a?.byEpoch.get(headline.epoch)?.rank;
    if (rank !== undefined && rank.rank !== null && rank.validators !== null && rank.validators > 0) {
      const rankLine = document.createElement("div");
      rankLine.className = "vf-band-subdetail";
      // Epoch tag only when the figure above is trailing (basis differs);
      // in the single-epoch fallback the detail line already states the epoch.
      const rankEpochTag = useTrailing ? ` · epoch ${headline.epoch}` : "";
      rankLine.textContent =
        `vote-credit rank ${formatCount(rank.rank)} of ${formatCount(rank.validators)}${rankEpochTag}`;
      cell1.append(rankLine);
    }

    // Cell 2: our position in the cluster's base-yield distribution. Reads the
    // trailing-days median of our_base_percentile_pct (robust; redundant with
    // the position tab's full history one click away), falling back to the
    // single headline epoch when the band is single-epoch. Zero served
    // (including net-only mode, whose base percentile column does not exist)
    // renders written absence — never a dash, never 0.
    const cell2 = document.createElement("div");
    cell2.className = "vf-band-cell";
    const label2 = document.createElement("div");
    label2.className = "vf-band-label";
    label2.textContent = "cluster position";
    const value2 = document.createElement("div");
    value2.className = "vf-band-value vf-mono";
    cell2.append(label2, value2);
    // Cell 2 shares the band's time basis: the trailing-days median when the
    // rest of the band is trailing (useTrailing), else the single headline
    // epoch's percentile — so the card never mixes "trailing 30 days" here
    // with "epoch N" in cell 1 (audit MEDIUM). "% above cluster average" is
    // intentionally NOT shown: the mean is depressed by delinquent/near-zero
    // validators, so a "% above average" reads soft (mostly beating dead
    // validators); the front leads with the robust percentile and the average
    // ratio lives in the tooltip for whoever drills in. (operator decision)
    if (useTrailing) {
      const posStat = trailingMedianByDays(a?.ourBasePercentile ?? [], durations, BAND_TRAILING_DAYS);
      if (posStat.median === null) {
        // windowEpochs === 0 means no epoch carried a duration to place in the
        // window (e.g. an API serving no /apy durations); name plain absence
        // rather than a bogus day count.
        value2.textContent =
          posStat.windowEpochs === 0 ? "not served" : `not served in the ${trailingSpanLabel}`;
      } else {
        value2.textContent = `${ordinal(Math.round(posStat.median))} percentile`;
        const detail2 = document.createElement("div");
        detail2.className = "vf-band-detail";
        detail2.textContent =
          posStat.served === posStat.windowEpochs
            ? `median over ${trailingSpanLabel}`
            : `median over ${trailingSpanLabel} (${posStat.served} served)`;
        cell2.append(detail2);
      }
    } else {
      const pctAtHeadline = a?.byEpoch.get(headline.epoch)?.cluster?.our_base_percentile_pct ?? null;
      if (pctAtHeadline === null) {
        value2.textContent = "not served";
      } else {
        value2.textContent = `${ordinal(Math.round(pctAtHeadline))} percentile`;
        const detail2 = document.createElement("div");
        detail2.className = "vf-band-detail";
        detail2.textContent = `epoch ${headline.epoch}`;
        cell2.append(detail2);
      }
    }

    // Cell 3: vs cluster median. Economic magnitude takes the value tier and
    // the bps ratio is demoted to the detail: both strings restate the same
    // served delta (sol = deltaPp * 10, a documented derivation), so which
    // one leads is a pure representation choice — and a reader deciding in
    // seconds reads the value tier and skips the muted one. A relative unit
    // there hands them something a delegator cannot price; SOL / 1k SOL / yr
    // is the only figure here denominated in money a desk books.
    //
    // Cell 4 deliberately keeps bps in its value tier (operator decision):
    // its branches emit a bound and an approximation ("< 0.01", "~0.4"), and
    // a bound at headline weight reads as absence rather than as the
    // tight-gap fact it is. That cell's claim is tightness, which is what bps
    // expresses; the unit asymmetry between the two is the accepted price.
    const deltaPp = ourPct - p50Pct;
    const cell3 = document.createElement("div");
    cell3.className = "vf-band-cell";
    const label3 = document.createElement("div");
    label3.className = "vf-band-label";
    label3.textContent = `vs cluster median${basisSuffix}`;
    const value3 = document.createElement("div");
    value3.className = "vf-band-value vf-mono";
    value3.textContent = formatEconomicDeltaSigned(deltaPp);
    const detail3 = document.createElement("div");
    detail3.className = "vf-band-detail";
    detail3.textContent = formatBps(deltaPp);
    cell3.append(label3, value3, detail3);

    // Cell 4: vs cluster best — signed end to end (ours can legitimately
    // exceed the served max; a delegator-APY reconstruction riding above
    // the cluster max has happened on real historical epochs). Three
    // branches, no clamping: ours below max keeps the positive "within"
    // tightness framing; ours above max states the surplus directly;
    // an exact tie states it in words. Base-yield percentile rank
    // demoted likewise regardless of branch.
    const deltaToMaxPp = ourPct - maxPct;
    const cell4 = document.createElement("div");
    cell4.className = "vf-band-cell";
    const label4 = document.createElement("div");
    label4.className = "vf-band-label";
    label4.textContent = `vs cluster best${basisSuffix}`;
    const value4 = document.createElement("div");
    value4.className = "vf-band-value vf-mono";
    const detail4 = document.createElement("div");
    detail4.className = "vf-band-detail";
    if (deltaToMaxPp > 0) {
      value4.textContent = formatAboveBestBps(deltaToMaxPp);
      detail4.textContent = formatEconomicDeltaSigned(deltaToMaxPp);
    } else if (deltaToMaxPp === 0) {
      value4.textContent = "at cluster best";
      detail4.textContent = "0.00 SOL / 1k SOL / yr";
    } else {
      value4.textContent = formatWithinBps(-deltaToMaxPp);
      detail4.textContent = formatEconomicGapApprox(-deltaToMaxPp);
    }
    cell4.append(label4, value4, detail4);

    const clusterAtHeadline = a?.byEpoch.get(headline.epoch)?.cluster;
    const pct = clusterAtHeadline?.our_base_percentile_pct ?? null;
    const percentileRank = clusterAtHeadline?.our_base_rank ?? null;
    const totalM = clusterAtHeadline?.distribution_infl_validators ?? null;
    const hasPercentileFact = inBase && pct !== null && percentileRank !== null && totalM !== null;
    if (hasPercentileFact && percentileRank !== null && totalM !== null) {
      const subdetail4 = document.createElement("div");
      subdetail4.className = "vf-band-subdetail";
      const yieldRankEpochTag = useTrailing ? ` · epoch ${headline.epoch}` : "";
      subdetail4.textContent =
        `base-yield rank ${formatCount(percentileRank)} of ${formatCount(totalM)}${yieldRankEpochTag}`;
      cell4.append(subdetail4);
    }

    // Reading order: headline -> money vs median -> money vs best -> position.
    // The ordinal is demoted out of reading position 2, where it preceded both
    // economic-magnitude cells and contradicted the band's own rule ("never a
    // top-X% headline value anywhere in the band") from the value tier. The
    // position group (3, 4, 2) stays DOM-contiguous, so the active-tab accent
    // still reads as one unbroken segmented run, and #wireBandTab is per-cell.
    band.append(cell1, cell3, cell4, cell2);

    // Cell 5: net incl MEV + the MEV component derived from it, base mode
    // only, null-safe (absent when the net value at this epoch is
    // unavailable — never a 0 or a placeholder dash). Folded into the
    // band's own cell grammar instead of a full-width row below it (the
    // row wasted a whole line while the band itself still had unused
    // width). Same violet as the chart's net-dotted line and the
    // tooltip's net swatch (ink system: one hue per meaning, everywhere).
    let cell5: HTMLDivElement | null = null;
    if (inBase && a !== null) {
      // MEV settles a beat AFTER inflation, so the base headline advances to an
      // epoch whose net is still pending (delegator_apy_pct null) at every
      // epoch boundary. Keying this cell off the base headline blanked it
      // entirely during that gap. Instead track the latest epoch whose net AND
      // base are both served (our own values, no cluster needed).
      let ni = -1;
      for (let i = a.epochs.length - 1; i >= 0; i -= 1) {
        if (a.our[i] !== null && a.ourBase[i] !== null) {
          ni = i;
          break;
        }
      }
      const netVal: number | null = ni === -1 ? null : (a.our[ni] ?? null);
      const mevPp = ni === -1 ? null : mevComponentPp(netVal, a.ourBase[ni] ?? null);
      if (ni !== -1 && netVal !== null && mevPp !== null) {
        const netEpoch = a.epochs[ni] ?? headline.epoch;
        const lag = headline.epoch - netEpoch;
        cell5 = document.createElement("div");
        cell5.className = "vf-band-cell";
        const label5 = document.createElement("div");
        label5.className = "vf-band-label vf-band-label-swatch";
        const swatch = document.createElement("span");
        swatch.className = "vf-band-swatch";
        swatch.style.background = this.#theme.netLine;
        label5.append(swatch, document.createTextNode("net incl MEV"));
        const value5 = document.createElement("div");
        value5.className = "vf-band-value vf-mono";
        const detail5 = document.createElement("div");
        detail5.className = "vf-band-detail";
        if (lag > MEV_MAX_LAG_EPOCHS) {
          // Settled net is too far behind the headline to present as current:
          // state pending + the last settled epoch, never a stale figure.
          value5.textContent = "pending";
          detail5.textContent = `last settled epoch ${netEpoch}`;
        } else {
          value5.textContent = formatPct(netVal);
          // Tag the epoch when it lags the base headline OR when the base
          // figure above is trailing (useTrailing) -- either way this cell is a
          // single epoch, so the epoch is stated to keep it from reading as
          // net = trailing-base + component (audit MEDIUM composability).
          const netEpochTag = useTrailing || netEpoch !== headline.epoch ? ` · epoch ${netEpoch}` : "";
          detail5.textContent = `MEV component ${formatSignedPp(mevPp)}${netEpochTag}`;
        }
        cell5.append(label5, value5, detail5);
        band.append(cell5);
      }
    }

    // View tabs (base mode only): the five cells act as three view
    // selectors — cell 1 the base chart (default), cells 2-4 the
    // percentile-position history, cell 5 the MEV-component bars.
    // Net-only mode has no alternate views to offer (their columns are
    // base-mode data), so its cells stay inert.
    if (inBase) {
      this.#wireBandTab(cell1, "base");
      for (const cell of [cell2, cell3, cell4]) {
        this.#wireBandTab(cell, "position");
      }
      if (cell5 !== null) {
        this.#wireBandTab(cell5, "mev");
      }
    }
  }

  /** Makes one answer-band cell a view selector: click/Enter/Space activates, aria-pressed + accent underline mark the active group. */
  #wireBandTab(cell: HTMLElement, view: ChartView): void {
    cell.classList.add("vf-band-tab");
    cell.tabIndex = 0;
    cell.setAttribute("role", "button");
    // Additive description only: the statistic text stays the accessible
    // NAME (an aria-label would hide the measured value from AT users);
    // this states what activation does. Cells sharing a view read as one
    // selector group by their identical description.
    cell.setAttribute("aria-description", VIEW_DESCRIPTIONS[view]);
    const active = this.#view === view;
    cell.setAttribute("aria-pressed", String(active));
    cell.classList.toggle("vf-band-tab-active", active);
    cell.addEventListener("click", () => this.#setView(view));
    cell.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        this.#setView(view);
      }
    });
  }

  /**
   * Switches the rendered plot. The dataZoom window persists (#zoom
   * is already live-captured); pins belong to the base view and are
   * cleared so none is left hovering over a different plot.
   */
  #setView(view: ChartView): void {
    if (view === this.#view) {
      return;
    }
    const leavingIncome = this.#view === "income";
    this.#view = view;
    this.#pins?.destroy();
    if (leavingIncome) {
      // A band-tab exit from the income view applies the same hygiene as
      // the toggle: no clicked-epoch mark or panel outlives the view.
      this.#incomePinnedEpoch = null;
      this.#drilldown?.hide();
    }
    this.#renderFull();
    // Re-render replaced the band cells; keep keyboard flow on the newly
    // active selector.
    this.#bandEl?.querySelector<HTMLElement>(".vf-band-tab-active")?.focus({ preventScroll: true });
  }

  /** Coverage figures of the latest epoch whose cluster distribution the gate withheld (on `basis`), or null if there is no distribution at all. */
  #latestGatedCoverage(
    a: Assembled | null,
    basis: StatsBasis,
  ): { measured: number; total: number | null } | null {
    if (a === null) {
      return null;
    }
    for (let i = a.epochs.length - 1; i >= 0; i -= 1) {
      const epoch = a.epochs[i];
      if (epoch === undefined) {
        continue;
      }
      const cluster = a.byEpoch.get(epoch)?.cluster;
      const cov = basis === "base" ? distributionCoverageInfl(cluster) : distributionCoverage(cluster);
      if (cov !== null && !cov.sufficient) {
        return { measured: cov.measured, total: cov.total };
      }
    }
    return null;
  }

  #ariaSummary(headline: HeadlineEpoch | null, a: Assembled | null): string {
    const title = this.#headerTitleText(a);
    if (headline === null) {
      return this.#hasCoveredFor(a)
        ? `${title}: no validator APY at a covered epoch.`
        : `${title}: no fully covered epoch in the selected range.`;
    }
    return (
      `${title}: ${headline.ourPct.toFixed(2)} percent at epoch ${headline.epoch}, ` +
      `${formatDeltaWords(headline.ourPct - headline.p50)}.`
    );
  }
}
